import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import './kids.css';
import { Link, NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  sendAdminMagicLink,
  sendParentMagicLink,
  signInAdminWithGoogle,
  signInParent,
  signInParentWithGoogle,
  signOut,
  signUpParent,
} from './lib/authApi';
import { appEnv } from './lib/env';
import { useSession } from '@/hooks/useSession';
import {
  AuthCardView,
  CardGrid,
  ChildAccessRequestPageView,
  Footer,
  PathSelectionPageView,
  ProfileSetupPageView,
} from './onboardingViews';
import {
  ActivityCard,
  AdminAuditLogView,
  AdminLoginView,
  AdminReportsView,
  AdminTablePage,
  ChildProgressView,
  DashboardHeader,
  ManageChildrenView,
  MediaLibraryView,
  QuestionBuilderView,
  ModerationReviewView,
  ParentDashboardView,
  ParentSettingsView,
  SaveNotice,
  SectionHeader,
  StatsGrid,
} from './parentViews';
import {
  createChildProfile,
  completeQuizAttempt,
  approveChildAccessRequest,
  isCurrentUserKidsAdmin,
  listAgeGroups,
  listAdminLearningModules,
  listAdminQuizzes,
  listActiveAvatars,
  listChildAccessRequests,
  listChildProfiles,
  listChildNotifications,
  loadLessonBySlug,
  loadAdminQuizPreview,
  listPublishedQuizzes,
  loadChildProgress,
  loadKidsCatalog,
  loadParentProgressSummary,
  loadQuizWithQuestions,
  archiveAdminQuiz,
  saveLearningArticleDraft,
  saveFamilySettings,
  saveAvatar,
  saveBadge,
  saveFeatureFlag,
  saveLearningPath,
  saveLearningModule,
  saveMediaMetadata,
  saveLesson,
  saveLessonProgress,
  markNotificationRead,
  publishAdminQuiz,
  saveQuizCategory,
  saveQuizDraft,
  saveQuizQuestionDraft,
  saveRewardItem,
  selectLearningPath,
  declineChildAccessRequest,
  submitChildAccessRequest,
  submitParentRequest,
  updateAdminParentRequest,
  uploadMediaAsset,
  type KidsCatalog,
  type AdminQuizReadinessRow,
  type ParentProgressSummary,
} from './lib/kidsApi';
import {
  formatDateTime,
  getAuditSummary,
  getErrorMessage,
  type AdminLoadState,
  type AdminSaveState,
  toSlug,
  useAdminAuditRows,
  useAdminArticleRows,
  useAdminAvatarRows,
  useAdminBadgeRows,
  useAdminCategories,
  useAdminCategoryRows,
  useAdminFeatureFlagRows,
  useAdminGuard,
  useAdminLearningModuleRows,
  useAdminLearningPathRows,
  useAdminLessonRows,
  useAdminMediaRows,
  useAdminModerationRows,
  useAdminQuizRows,
  useAdminRewardItemRows,
  useAdminReportRows,
} from './adminData';
import type {
  KidsAgeGroup,
  KidsAvatar,
  KidsBadge,
  KidsFeatureFlag,
  KidsLanguage,
  KidsLearningPath,
  KidsLearningModule,
  KidsLesson,
  KidsMediaRelation,
  KidsMediaSummary,
  KidsMediaAsset,
  KidsNotification,
  KidsParentRequestType,
  KidsParentRequest,
  KidsChildAccessRequest,
  KidsQuiz,
  KidsQuizCategory,
  KidsQuizAttemptResult,
  KidsQuizOption,
  KidsQuizQuestion,
  KidsRewardItem,
} from './types';

type ShellLink = {
  label: string;
  to: string;
};

type CardGridItem = {
  title: string;
  text: string;
  imageUrl?: string;
  imageAlt?: string;
};

type QuizCardItem = {
  title: string;
  slug: string;
  category: string;
  difficulty: string;
  count: string;
  stars: string;
  imageUrl?: string;
  imageAlt?: string;
};

type BadgeCardItem = {
  title: string;
  text: string;
  status: string;
  imageUrl?: string;
  imageAlt?: string;
};

function mediaSummary(media: KidsMediaRelation | undefined): KidsMediaSummary | null {
  if (!media) return null;
  return Array.isArray(media) ? media[0] ?? null : media;
}

function mediaImageUrl(media: KidsMediaRelation | undefined): string | undefined {
  const summary = mediaSummary(media);
  if (!summary) return undefined;
  if (summary.public_url) return summary.public_url;
  if (!summary.path || !appEnv.supabaseUrl) return undefined;
  return `${appEnv.supabaseUrl}/storage/v1/object/public/kids-media/${summary.path}`;
}

const publicLinks: ShellLink[] = [
  { label: 'Home', to: '/kids/' },
  { label: 'Learn', to: '/kids/app/learn' },
  { label: 'Quizzes', to: '/kids/app/quizzes' },
  { label: 'Parent Guide', to: '/kids/parent-guide' },
  { label: 'FAQ', to: '/kids/faq' },
];

const kidLinks: ShellLink[] = [
  { label: 'Home', to: '/kids/app' },
  { label: 'Learn', to: '/kids/app/learn' },
  { label: 'Quizzes', to: '/kids/app/quizzes' },
  { label: 'Rewards', to: '/kids/app/rewards' },
  { label: 'Notifications', to: '/kids/app/notifications' },
  { label: 'Leaderboard', to: '/kids/app/leaderboard' },
  { label: 'Profile', to: '/kids/app/profile' },
];

const parentLinks: ShellLink[] = [
  { label: 'Dashboard', to: '/kids/parent' },
  { label: 'Children', to: '/kids/parent/children' },
  { label: 'Progress', to: '/kids/parent/children/amina' },
  { label: 'Settings', to: '/kids/parent/settings' },
];

const adminLinks: ShellLink[] = [
  { label: 'Dashboard', to: '/kids/admin' },
  { label: 'Quizzes', to: '/kids/admin/quizzes' },
  { label: 'Media Library', to: '/kids/admin/media' },
  { label: 'Learning Content', to: '/kids/admin/articles' },
  { label: 'Reports', to: '/kids/admin/reports' },
  { label: 'Moderation', to: '/kids/admin/moderation' },
  { label: 'Audit', to: '/kids/admin/audit' },
];

const featureCards = [
  ['Quran Quizzes', 'Quiz cards with categories, difficulty, rewards, and progress.'],
  ['Surah Explorer', 'A bright learning path for Surah names, Arabic names, ayah counts, and key facts.'],
  ['Memorization Support', 'Short review prompts, ayah goals, and quiz follow-ups that support steady revision.'],
  ['Parent Tracking', 'Parent dashboards show quiz history, strengths, needs practice, and recommendations.'],
  ['Safe Leaderboards', 'Privacy-friendly ranks using avatars and nicknames instead of personal details.'],
  ['Fresh Content', 'New quizzes, articles, media, avatars, badges, and reports can be managed by admins.'],
];

const landingHighlights = [
  ['Create child profiles', 'Set age group, avatar, language, and daily goal for each child.'],
  ['Choose learning paths', 'Start with Surah names, meanings, Islamic basics, stories, or duas.'],
  ['Practice through quizzes', 'Each quiz includes explanations, rewards, and gentle review moments.'],
  ['Celebrate progress', 'Stars, streaks, badges, and parent-friendly reports encourage consistent Quran study.'],
];

const parentTips = [
  'Sit with your child for the first few sessions.',
  'Ask what they learned after each quiz.',
  'Review explanations together, especially after mistakes.',
  'Use streaks as encouragement, not pressure.',
  'Check the parent dashboard once a week.',
];

const quizCards = [
  ['Surah Names', 'Surahs', 'Easy', '10 questions', '100 stars'],
  ['Ayah Meanings', 'Meanings', 'Medium', '12 questions', '120 stars'],
  ['Daily Duas', 'Islamic Basics', 'Easy', '8 questions', '80 stars'],
  ['Prophets in the Quran', 'Stories', 'Medium', '10 questions', '100 stars'],
];

const studyRewardCards = [
  ['Al-Fatihah Revision Card', 'Unlocked at 90 stars', 'Revision prompt', 'Unlocked'],
  ['Morning Dua Checklist', 'Pass 2 quizzes', 'Printable reminder', 'Locked'],
  ['Surah Mulk Goal Badge', 'Complete a lesson', 'Memorization milestone', 'Locked'],
];

const learningCards = [
  ['Surahs', 'Explore Quran chapters with simple facts and progress.'],
  ['Meanings', 'Learn child-friendly meanings from selected Quran words.'],
  ['Vocabulary', 'Build Arabic recognition with gentle repetition.'],
  ['Prophets', 'Read Quran-based prophet lessons with related quizzes.'],
  ['Daily Duas', 'Practice short daily duas and when to use them.'],
  ['Islamic Basics', 'Learn foundational topics through small steps.'],
];

const surahCards = [
  ['1', 'Al-Fatihah', 'الفاتحة', '7 ayahs', '80%', 'The Opening and a guide for prayer.'],
  ['112', 'Al-Ikhlas', 'الإخلاص', '4 ayahs', '60%', 'Learn about the oneness of Allah.'],
  ['113', 'Al-Falaq', 'الفلق', '5 ayahs', '40%', 'A short Surah for seeking protection.'],
  ['114', 'An-Nas', 'الناس', '6 ayahs', '40%', 'A short Surah about asking Allah for protection.'],
];

const rewardCards = [
  ['First Quiz', 'Unlocked after completing your first Quran quiz.', 'Unlocked'],
  ['7 Correct Answers', 'Answer 7 questions correctly in one quiz.', 'Unlocked'],
  ['Surah Explorer', 'Explore 5 Surahs in the knowledge base.', '3 of 5'],
  ['Week Streak', 'Study 7 days in a row.', '5 of 7'],
  ['Meaning Master', 'Complete 3 meanings quizzes.', 'Locked'],
  ['Dua Review Badge', 'Finish 3 dua quizzes with review.', 'Coming soon'],
];

const leaderboardRows = [
  ['1', 'Noor', '760 stars', 'Surah Star'],
  ['2', 'Maryam', '690 stars', 'Ayah Reader'],
  ['3', 'Sami', '620 stars', 'Dua Learner'],
  ['4', 'Huda', '510 stars', 'Quiz Explorer'],
];

const parentChildren = [
  ['Maryam', '7-10', 'Ayah Reader', 'Level 3', '10 min daily', 'Active'],
  ['Huda', '5-6', 'Dua Learner', 'Level 1', '5 min daily', 'Needs setup'],
];

const progressRows = [
  ['Surah Names Quiz', '8/10', '80 stars', 'Completed today'],
  ['Al-Fatihah Meanings', '6/8', '60 stars', 'Needs review'],
  ['Morning Dua Review', '4/6', '40 stars', 'Practice planned'],
];

const recommendationCards = [
  ['Review Al-Fatihah together', 'Ask your child why Al-Fatihah is called The Opening, then let them explain it back in their own words.'],
  ['Practice short duas', 'Choose one daily dua after dinner and repeat it gently for three days.'],
  ['Keep sessions short', 'A focused 10 minute Quran session is enough for today. Consistency matters more than length.'],
];

const mediaRows = [
  ['al-fatihah-family-study.webp', 'Website hero', 'Landing Page / Hero Section', 'Active'],
  ['ayah-reader-avatar.webp', 'Avatar', 'Child Profile / Avatar Library', 'Active'],
  ['dua-review-badge.webp', 'Badge', 'Rewards / Badge Grid', 'Active'],
  ['surah-names-cover.webp', 'Quiz cover', 'Quiz List / Surah Names Quiz', 'Needs alt text'],
];

export function KidsApp() {
  return (
    <Routes>
      <Route element={<PublicShell />}>
        <Route index element={<LandingPage />} />
        <Route path="faq" element={<FaqPage />} />
        <Route path="parent-guide" element={<ParentGuidePage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="signup" element={<SignupPage />} />
        <Route path="request-parent" element={<ChildAccessRequestPage />} />
        <Route
          path="onboarding/profile"
          element={
            <RequireKidsParentAuth>
              <ProfileSetupPage />
            </RequireKidsParentAuth>
          }
        />
        <Route
          path="onboarding/path"
          element={
            <RequireKidsParentAuth>
              <PathSelectionPage />
            </RequireKidsParentAuth>
          }
        />
        <Route path="goodbye" element={<GoodbyePage />} />
      </Route>

      <Route path="app" element={<KidShell />}>
        <Route index element={<KidDashboard />} />
        <Route path="learn" element={<LearnHome />} />
        <Route path="lessons/:lessonSlug" element={<LessonDetailPage />} />
        <Route path="surahs" element={<SurahExplorer />} />
        <Route path="surahs/:surahId" element={<SurahDetail />} />
        <Route path="quizzes" element={<QuizzesHome />} />
        <Route path="quizzes/:quizSlug" element={<QuizStart />} />
        <Route path="quizzes/:quizSlug/question" element={<QuizQuestion />} />
        <Route path="quizzes/:quizSlug/correct" element={<CorrectFeedback />} />
        <Route path="quizzes/:quizSlug/keep-going" element={<KeepGoingFeedback />} />
        <Route path="quizzes/:quizSlug/results" element={<QuizResults />} />
        <Route path="rewards" element={<RewardsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="streaks" element={<StreaksPage />} />
        <Route path="leaderboard" element={<LeaderboardPage />} />
        <Route path="profile" element={<KidProfilePage />} />
        <Route path="switch" element={<SwitchProfilePage />} />
      </Route>

      <Route path="parent" element={<ParentShell />}>
        <Route index element={<ParentDashboard />} />
        <Route path="children" element={<ManageChildren />} />
        <Route path="children/:profileId" element={<ChildProgress />} />
        <Route path="settings" element={<ParentSettings />} />
      </Route>

      <Route path="admin/login" element={<AdminLogin />} />
      <Route path="admin" element={<AdminShell />}>
        <Route index element={<AdminDashboard />} />
        <Route path="quizzes" element={<QuizCmsList />} />
        <Route path="quizzes/:quizId" element={<CreateEditQuiz />} />
        <Route path="questions" element={<QuestionBuilder />} />
        <Route path="quiz-preview" element={<QuizPreview />} />
        <Route path="categories" element={<CategoryManagement />} />
        <Route path="paths" element={<LearningPathCms />} />
        <Route path="modules" element={<LearningModuleCms />} />
        <Route path="lessons" element={<LessonCms />} />
        <Route path="avatars" element={<AvatarCms />} />
        <Route path="badges" element={<BadgeRewardCms />} />
        <Route path="reward-items" element={<RewardItemCms />} />
        <Route path="feature-flags" element={<FeatureFlagCms />} />
        <Route path="articles" element={<KnowledgeBaseCms />} />
        <Route path="articles/:articleId" element={<CreateEditArticle />} />
        <Route path="media" element={<MediaLibrary />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="moderation" element={<ModerationReview />} />
        <Route path="audit" element={<AdminAuditLog />} />
      </Route>

      <Route path="*" element={<Navigate to="/kids/" replace />} />
    </Routes>
  );
}

function PublicShell() {
  const [theme, toggleTheme] = useKidsTheme();

  return (
    <main className="site-shell">
      <header className="site-header">
        <Link className="brand-mark" to="/kids/">
          <span>AK</span>
          Alhudah Kids
        </Link>
        <nav aria-label="Public navigation">
          {publicLinks.map((link) => (
            <NavLink key={link.to} to={link.to}>
              {link.label}
            </NavLink>
          ))}
        </nav>
        <div className="header-actions">
          <button className="theme-toggle" onClick={toggleTheme} type="button" aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}>
            <span>{theme === 'dark' ? 'Night' : 'Day'}</span>
            <strong>{theme === 'dark' ? '☾' : '☀'}</strong>
          </button>
          <Link className="ghost-link" to="/kids/login">
            Parent Login
          </Link>
          <Link className="button button-primary" to="/kids/signup">
            Start Learning
          </Link>
        </div>
      </header>
      <Outlet />
      <Footer />
    </main>
  );
}

function useKidsTheme(): ['light' | 'dark', () => void] {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const stored = window.localStorage.getItem('alhudah.kids.theme');
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  });

  useEffect(() => {
    document.documentElement.dataset.kidsTheme = theme;
    window.localStorage.setItem('alhudah.kids.theme', theme);
  }, [theme]);

  return [theme, () => setTheme((current) => (current === 'dark' ? 'light' : 'dark'))];
}

function KidShell() {
  return (
    <main className="app-shell child-shell">
      <aside className="side-nav">
        <Link className="brand-mark" to="/kids/">
          <span>AK</span>
          Alhudah Kids
        </Link>
        <nav aria-label="Child navigation">
          {kidLinks.map((link) => (
            <NavLink key={link.to} end={link.to === '/app'} to={link.to}>
              {link.label}
            </NavLink>
          ))}
        </nav>
        <Link className="button button-secondary" to="/kids/app/switch">
          Switch Explorer
        </Link>
      </aside>
      <section className="workspace">
        <Outlet />
      </section>
    </main>
  );
}

function ParentShell() {
  return (
    <main className="site-shell parent-shell">
      <header className="site-header">
        <Link className="brand-mark" to="/kids/">
          <span>AK</span>
          Parent Portal
        </Link>
        <nav aria-label="Parent navigation">
          {parentLinks.map((link) => (
            <NavLink key={link.to} end={link.to === '/parent'} to={link.to}>
              {link.label}
            </NavLink>
          ))}
        </nav>
        <Link className="button button-primary" to="/kids/app/switch">
          Open Child Mode
        </Link>
      </header>
      <section className="page-frame">
        <Outlet />
      </section>
    </main>
  );
}

function AdminShell() {
  const guard = useAdminGuard();
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut().catch(() => undefined);
    navigate('/kids/admin/login');
  }

  if (guard.state === 'checking') {
    return <LoadingPanel title="Checking Kids CMS access..." />;
  }

  if (guard.state === 'denied') {
    return (
      <section className="auth-layout admin-login">
        <article className="auth-card">
          <p className="eyebrow">Kids CMS</p>
          <h1>CMS access required.</h1>
          <p>Sign in with an approved content manager account for Alhudah Kids CMS.</p>
          <Link className="button button-primary full" to="/kids/admin/login">
            Go to Kids CMS Login
          </Link>
          <Link className="ghost-link" to="/kids/">
            Back to website
          </Link>
        </article>
      </section>
    );
  }

  return (
    <main className="app-shell admin-shell">
      <aside className="side-nav admin-nav">
        <Link className="brand-mark" to="/kids/admin">
          <span>AK</span>
          Kids CMS
        </Link>
        <nav aria-label="Admin navigation">
          {adminLinks.map((link) => (
            <NavLink key={link.to} end={link.to === '/kids/admin'} to={link.to}>
              {link.label}
            </NavLink>
          ))}
          <div className="nav-section-label">Content</div>
          <NavLink to="/kids/admin/categories">Categories</NavLink>
          <NavLink to="/kids/admin/paths">Learning Paths</NavLink>
          <NavLink to="/kids/admin/modules">Learning Modules</NavLink>
          <NavLink to="/kids/admin/lessons">Lessons</NavLink>
          <div className="nav-section-label">Gamification</div>
          <NavLink to="/kids/admin/avatars">Avatars</NavLink>
          <NavLink to="/kids/admin/badges">Badges</NavLink>
          <NavLink to="/kids/admin/reward-items">Reward Items</NavLink>
          <NavLink to="/kids/admin/feature-flags">Feature Flags</NavLink>
        </nav>
        <button className="button button-secondary full" onClick={handleSignOut} type="button">
          Sign Out
        </button>
      </aside>
      <section className="workspace admin-workspace">
        <header className="cms-topbar">
          <div className="cms-search" role="search">
            <span aria-hidden="true">Search</span>
            <input aria-label="Search Kids CMS" placeholder="Search quizzes, lessons, media, or logs..." type="search" />
          </div>
          <div className="cms-topbar-actions">
            <span className="cms-env-pill">Production</span>
            <Link className="button button-primary" to="/kids/admin/quizzes/new">
              Create New
            </Link>
          </div>
        </header>
        <Outlet />
      </section>
    </main>
  );
}

function LandingPage() {
  const catalog = useCatalogPreview();
  const homepageCards: CardGridItem[] = catalog?.categories.length
    ? catalog.categories.map((category) => {
        const categoryMedia = mediaSummary(category.media);
        return {
          title: category.title,
          text: category.description ?? 'A learning category for Quran practice.',
          imageUrl: mediaImageUrl(category.media),
          imageAlt: categoryMedia?.alt_text ?? category.title,
        };
      })
    : featureCards.map(([title, text]) => ({ title, text }));
  const articleCards: CardGridItem[] = catalog?.articles.length
    ? catalog.articles.map((article) => {
        const cover = mediaSummary(article.cover_media);
        return {
          title: article.title,
          text: article.summary ?? 'A child-friendly learning article from the knowledge base.',
          imageUrl: mediaImageUrl(article.cover_media),
          imageAlt: cover?.alt_text ?? article.title,
        };
      })
    : [
        { title: 'Surah Explorer', text: 'Learn Surah names, meanings, ayah counts, and gentle reflection prompts.' },
        { title: 'Daily Duas', text: 'Short, practical duas children can practice with parents.' },
        { title: 'Quran Words', text: 'Friendly vocabulary practice for Arabic recognition and meaning.' },
      ];
  const featuredQuizCards = catalog?.categories.length
    ? catalog.categories.slice(0, 4).map((category, index) => ({
        title: category.title,
        category: category.title,
        difficulty: ['Easy', 'Medium', 'Easy', 'Medium'][index] ?? 'Easy',
        count: `${8 + index * 2} questions`,
        stars: `${80 + index * 20} stars`,
        imageUrl: mediaImageUrl(category.media),
        imageAlt: mediaSummary(category.media)?.alt_text ?? category.title,
      }))
    : quizCards.map(([title, category, difficulty, count, stars]) => ({
        title,
        category,
        difficulty,
        count,
        stars,
        imageUrl: undefined,
        imageAlt: title,
      }));
  const featureFlags = catalog?.featureFlags ?? [];
  const backendCounts = useMemo(
    () => [
      ['Quiz categories', catalog?.categories.length ?? 4],
      ['Learning modules', catalog?.modules.length ?? 5],
      ['Lessons', catalog?.lessons.length ?? 1],
      ['Reward items', catalog?.rewardItems.length ?? 3],
    ],
    [catalog],
  );

  return (
    <>
      <section className="hero-section kids-landing-hero">
        <div className="hero-copy">
          <p className="eyebrow">Adventure awaits</p>
          <h1>
            Quizzes, Surahs, and <span>Quran learning!</span>
          </h1>
          <p>
            Children explore Surahs, practice Quran quizzes, collect stars, and build steady study habits while
            parents guide every step.
          </p>
          <div className="button-row">
            <Link className="button button-primary" to="/kids/signup">
              Start My Journey
            </Link>
            <Link className="button button-secondary" to="/kids/app/quizzes">
              Browse Quizzes
            </Link>
          </div>
          <div className="trust-row">
            <span>Parent guided</span>
            <span>Quran focused</span>
            <span>Safe for kids</span>
            <span>Quiz rewards earned by learning</span>
          </div>
        </div>
        <div className="hero-art arcade-hero" aria-label="Alhudah Kids learning preview">
          <img className="hero-media" src="/kids/alhudah-world-hero.png" alt="Pixel art Alhudah World Quran learning adventure." />
          <div className="floating-card card-green">
            <strong>94%</strong>
            <span>Quiz accuracy</span>
          </div>
          <div className="floating-card card-yellow">
            <strong>120 stars</strong>
            <span>Study reward ready</span>
          </div>
        </div>
      </section>

      <SectionHeader
        eyebrow="Explore your world"
        title="Learning islands powered by the Kids backend."
        text="Categories, lessons, rewards, media, and roadmap features can come from the CMS while the page stays playful."
      />
      <CardGrid items={homepageCards} />

      <section className="quiz-showcase-section">
        <div>
          <p className="eyebrow">Pick your challenge</p>
          <h2>Quizzes connect learning, stars, and next steps.</h2>
          <p>Quiz cards can be loaded from the backend by category, difficulty, question count, rewards, and media.</p>
        </div>
        <div className="landing-quiz-grid">
          {featuredQuizCards.map((quiz) => (
            <article className="landing-quiz-card" key={quiz.title}>
              {quiz.imageUrl ? <img src={quiz.imageUrl} alt={quiz.imageAlt ?? quiz.title} /> : <div className="quiz-pixel-art" />}
              <span>{quiz.category}</span>
              <h3>{quiz.title}</h3>
              <p>Pass this challenge to earn stars and move forward on the learning path.</p>
              <footer>
                <small>{quiz.difficulty}</small>
                <small>{quiz.count}</small>
                <strong>{quiz.stars}</strong>
              </footer>
            </article>
          ))}
        </div>
      </section>

      <section className="games-reward-section">
        <div className="games-copy">
          <p className="eyebrow">Study rewards</p>
          <h2>Motivation stays tied to learning.</h2>
          <p>
            Children unlock revision prompts, dua checklists, and memorization badges by completing Quran quizzes and lessons.
            Parents can see reward activity and keep motivation tied to learning.
          </p>
          <div className="safety-list">
            <span>Quiz-gated rewards</span>
            <span>Revision focused</span>
            <span>Parent visible</span>
          </div>
        </div>
        <div className="arcade-board">
          {studyRewardCards.map(([title, condition, time, state]) => (
            <article className={state === 'Unlocked' ? 'arcade-tile unlocked' : 'arcade-tile'} key={title}>
              <div className="arcade-icon">{state === 'Unlocked' ? '★' : '▣'}</div>
              <h3>{title}</h3>
              <p>{condition}</p>
              <small>{time}</small>
            </article>
          ))}
        </div>
      </section>

      <section className="journey-section">
        <SectionHeader
          eyebrow="Learning path"
          title="A gentle journey from profile to progress."
          text="The flow stays parent-guided while children feel like explorers moving through a map."
        />
        <div className="journey-map">
          {landingHighlights.map(([title, text], index) => (
            <article key={title}>
              <span>{index + 1}</span>
              <strong>{title}</strong>
              <p>{text}</p>
            </article>
          ))}
          <article>
            <span>5</span>
            <strong>Unlock study rewards</strong>
            <p>Revision rewards appear only after Quran learning milestones are completed.</p>
          </article>
        </div>
      </section>

      <section className="parent-preview-panel">
        <div>
          <p className="eyebrow">Parent portal</p>
          <h2>Stay connected with their journey.</h2>
          <p>
            Real-time insights help parents review quiz accuracy, completed lessons, strengths, needs practice, and
            reward activity.
          </p>
          <div className="parent-stats">
            <span><strong>94%</strong> recent accuracy</span>
            <span><strong>12</strong> lessons completed</span>
            <span><strong>2</strong> study rewards unlocked this week</span>
          </div>
        </div>
        <div className="parent-recommendation">
          <strong>Recommended for tonight</strong>
          <p>Review Surah Al-Fatihah, then complete one short quiz together.</p>
          <Link className="button button-primary" to="/kids/parent-guide">Parent Guide</Link>
        </div>
      </section>

      <section className="knowledge-section">
        <SectionHeader
          eyebrow="Knowledge base"
          title="Fresh learning content from the CMS."
          text="Articles, modules, lessons, media, and feature flags can keep this landing page alive as the backend grows."
        />
        <CardGrid items={articleCards} />
        <div className="backend-metrics">
          {backendCounts.map(([label, value]) => (
            <span key={label}>
              <strong>{value}</strong>
              {label}
            </span>
          ))}
        </div>
      </section>

      {featureFlags.length ? (
        <section className="roadmap-section">
          <SectionHeader
            eyebrow="Coming next"
            title="A roadmap parents can understand."
            text="Feature availability is managed from the admin CMS."
          />
          <div className="module-grid">
            {featureFlags.map((flag) => (
              <article className={flag.status === 'active' ? 'module-card' : 'module-card locked'} key={flag.slug}>
                <span>{flag.status === 'active' ? 'Active' : 'Coming soon'}</span>
                <strong>{flag.title}</strong>
                <p>{flag.description ?? 'A planned feature for the Alhudah Kids experience.'}</p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section className="cta-band">
        <div>
          <p className="eyebrow">Join the adventure</p>
          <h2>Start a joyful Quran learning habit today.</h2>
        </div>
        <Link className="button button-primary" to="/kids/signup">
          Create your profile
        </Link>
      </section>
    </>
  );
}

function FaqPage() {
  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Parent FAQ"
        title="Clear answers before your child starts."
        text="Short, practical answers for safety, learning, progress, and multiple children."
      />
      <div className="faq-list">
        {[
          ['Is it safe for kids?', 'Children use avatars and parent-managed profiles. Leaderboards are privacy-safe and do not need full names.'],
          ['Is the content Quran-based?', 'The app is designed around Quran learning, Surah facts, meanings, duas, stories, and Islamic basics.'],
          ['How much time should my child spend daily?', 'Start with 5 to 10 minutes. Short, consistent sessions are better than long sessions that feel heavy.'],
          ['Can I track progress?', 'Yes. Parent dashboards show quiz history, streaks, badges, strengths, needs practice, and recommended next lessons.'],
          ['Can I add multiple children?', 'Yes. Each child has a separate profile, avatar, age group, learning path, goals, and progress history.'],
          ['Can I change the learning path later?', 'Yes. Parents can adjust learning paths as the child grows.'],
          ['How are quiz scores calculated?', 'Quiz attempts track answers, correct count, stars, XP, and completion status.'],
          ['Can teachers or admins create quizzes?', 'Yes. Admin tools manage quizzes, questions, categories, articles, badges, media, and moderation.'],
          ['Does it support Arabic, English, and Urdu?', 'The learning experience supports language preferences and dedicated Arabic display.'],
        ].map(([question, answer]) => (
          <details className="faq-item" key={question}>
            <summary>{question}</summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
      <section className="cta-band">
        <div>
          <p className="eyebrow">Still need help?</p>
          <h2>Parents can request changes or support from the guide page.</h2>
        </div>
        <Link className="button button-secondary" to="/kids/parent-guide">
          Open Parent Guide
        </Link>
      </section>
    </section>
  );
}

function ParentGuidePage() {
  const form = useParentRequestForm();

  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Parent Guide"
        title="A parent’s guide to Alhudah Kids."
        text="A full walkthrough for setting up profiles, choosing paths, supporting quiz learning, tracking progress, and requesting support."
      />
      <div className="guide-layout">
        <ol className="step-list long">
          <li>Create your parent account, verify email, and keep login details private.</li>
          <li>Add child profiles with name, age group, avatar, learning language, and daily goal.</li>
          <li>Choose a learning path based on age, confidence, and current Quran familiarity.</li>
          <li>Help your child start from the dashboard using Continue Learning and recommended quizzes.</li>
          <li>Review quiz explanations together and use mistakes as gentle learning moments.</li>
          <li>Use stars, XP, badges, and streaks to celebrate consistency instead of pressure.</li>
          <li>Check the parent dashboard weekly for accuracy, strengths, and needs practice.</li>
          <li>Manage multiple children separately so each journey stays personal.</li>
        </ol>
        <form className="support-form" onSubmit={form.handleSubmit} noValidate>
          <h2>Request a change or ask for help</h2>
          <label>
            Parent name
            <input
              name="parent_name"
              onChange={form.handleChange}
              placeholder="Your name"
              value={form.values.parent_name}
            />
            {form.errors.parent_name ? <span className="field-error">{form.errors.parent_name}</span> : null}
          </label>
          <label>
            Email address
            <input
              name="email"
              onChange={form.handleChange}
              placeholder="parent@example.com"
              type="email"
              value={form.values.email}
            />
            {form.errors.email ? <span className="field-error">{form.errors.email}</span> : null}
          </label>
          <label>
            Request type
            <select name="request_type" onChange={form.handleChange} value={form.values.request_type}>
              <option value="" disabled>
                Choose a request
              </option>
              <option value="content_correction">Content correction</option>
              <option value="quiz_issue">Quiz issue</option>
              <option value="suggest_topic">Suggest new topic</option>
              <option value="technical_support">Technical support</option>
              <option value="account_help">Account help</option>
              <option value="other">Other</option>
            </select>
            {form.errors.request_type ? <span className="field-error">{form.errors.request_type}</span> : null}
          </label>
          <label>
            Related page or quiz
            <input
              name="related_context"
              onChange={form.handleChange}
              placeholder="Optional page, quiz, or lesson name"
              value={form.values.related_context}
            />
          </label>
          <label>
            Message
            <textarea
              name="message"
              onChange={form.handleChange}
              placeholder="Tell us what you need in at least 20 characters."
              value={form.values.message}
            />
            {form.errors.message ? <span className="field-error">{form.errors.message}</span> : null}
          </label>
          <button className="button button-primary" disabled={form.status === 'submitting'} type="submit">
            {form.status === 'submitting' ? 'Submitting...' : 'Submit Request'}
          </button>
          {form.status === 'success' ? <p className="form-success">Thank you. Your request has been received.</p> : null}
          {form.status === 'error' ? (
            <p className="form-note">We could not send this request right now. Please check your connection and try again.</p>
          ) : null}
        </form>
      </div>
      <section className="parent-tips">
        <SectionHeader
          eyebrow="Parent + child coordination"
          title="Use Alhudah Kids as a shared learning habit."
          text="The website should support family connection, not just screen time."
        />
        <div className="tip-grid">
          {parentTips.map((tip) => (
            <article className="tip-card" key={tip}>
              {tip}
            </article>
          ))}
        </div>
      </section>
      <section className="progress-explainer">
        <div>
          <p className="eyebrow">Progress tracking</p>
          <h2>Know what each metric means.</h2>
          <p>Stars and badges celebrate effort. Accuracy and needs-practice areas show where parents can help gently.</p>
        </div>
        <StatsGrid stats={[['Stars', 'Effort'], ['Streak', 'Consistency'], ['Accuracy', 'Understanding'], ['Needs practice', 'Next focus']]} />
      </section>
    </section>
  );
}

function AboutPage() {
  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Mission & Safety"
        title="A warm Quran learning space families can trust."
        text="Alhudah Kids balances bright child-friendly learning with parent control, content review, and privacy-aware progress."
      />
      <CardGrid
        items={[
          ['Our mission', 'Make Quran learning joyful, consistent, and easy for young learners.'],
          ['Parent guided', 'Parents manage accounts, profiles, language preferences, goals, and progress.'],
          ['Child friendly', 'Lessons are short, visual, age-aware, and encouraging.'],
          ['Privacy aware', 'Children can use avatars and nicknames while parents control account access.'],
          ['Content review', 'Drafts, publishing, moderation, and parent feedback help keep learning content accurate.'],
          ['Media managed', 'Images, avatars, badges, and page references are organized from the media library.'],
        ]}
      />
      <section className="split-panel">
        <div>
          <p className="eyebrow">Content quality</p>
          <h2>Production content should be reviewed before publishing.</h2>
        </div>
        <p>
          Quiz questions, explanations, learning articles, and media references move through review before they appear
          to children. Parent requests provide a feedback loop for corrections, suggestions, and support.
        </p>
      </section>
    </section>
  );
}

function LoginPage() {
  return <AuthCardView mode="login" form={useAuthForm('login')} />;
}

function SignupPage() {
  return <AuthCardView mode="signup" form={useAuthForm('signup')} />;
}

function RequireKidsParentAuth({ children }: { children: JSX.Element }) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return (
      <section className="auth-layout">
        <article className="auth-card">
          <p className="form-note">Checking your parent session...</p>
        </article>
      </section>
    );
  }

  if (!session.data?.user) {
    const next = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate replace to={`/kids/login?next=${encodeURIComponent(next)}`} />;
  }

  return children;
}

function ChildAccessRequestPage() {
  return <ChildAccessRequestPageView form={useChildAccessRequestForm()} />;
}

function ProfileSetupPage() {
  const form = useChildProfileForm();
  return (
    <ProfileSetupPageView
      form={form}
      fallbackAgeGroups={[
        { id: '5-6', slug: '5-6', label: '5-6 years', min_age: 5, max_age: 6, reading_level: 1, default_difficulty: 'easy', sort_order: 10 },
        { id: '7-10', slug: '7-10', label: '7-10 years', min_age: 7, max_age: 10, reading_level: 2, default_difficulty: 'easy', sort_order: 20 },
        { id: '11-13', slug: '11-13', label: '11-13 years', min_age: 11, max_age: 13, reading_level: 3, default_difficulty: 'medium', sort_order: 30 },
      ]}
      fallbackAvatars={[
        { id: 'ayah-reader', slug: 'ayah-reader', title: 'Ayah Reader', media_id: null, age_group_id: null, color_key: 'emerald', is_active: true, sort_order: 10 },
      ]}
      mediaImageUrl={mediaImageUrl}
      mediaAltText={(media, fallback) => mediaSummary(media)?.alt_text ?? fallback}
    />
  );
}

function PathSelectionPage() {
  const catalog = useCatalogPreview();
  const paths = catalog?.learningPaths.length
    ? catalog.learningPaths
    : ([
        { id: 'beginner-quran-explorer', slug: 'beginner-quran-explorer', title: 'Beginner Quran Explorer', description: 'Recommended for new learners starting from the basics.', icon_key: null, media_id: null, sort_order: 1, is_active: true },
        { id: 'surah-names-facts', slug: 'surah-names-facts', title: 'Surah Names & Facts', description: 'Build recognition of Surah names, numbers, and themes.', icon_key: null, media_id: null, sort_order: 2, is_active: true },
        { id: 'meanings-vocabulary', slug: 'meanings-vocabulary', title: 'Meanings & Vocabulary', description: 'Learn simple meanings and useful Quran words.', icon_key: null, media_id: null, sort_order: 3, is_active: true },
        { id: 'islamic-basics', slug: 'islamic-basics', title: 'Islamic Basics', description: 'Practice foundational topics through gentle quizzes.', icon_key: null, media_id: null, sort_order: 4, is_active: true },
        { id: 'stories-of-prophets', slug: 'stories-of-prophets', title: 'Stories of Prophets', description: 'Explore lessons through story-based learning.', icon_key: null, media_id: null, sort_order: 5, is_active: true },
      ] satisfies KidsLearningPath[]);
  const selection = useLearningPathSelection(paths[0]?.id ?? '');
  const { selectedPathId, setSelectedPathId } = selection;

  useEffect(() => {
    if (paths.length && !paths.some((path) => path.id === selectedPathId)) {
      setSelectedPathId(paths[0].id);
    }
  }, [paths, selectedPathId, setSelectedPathId]);

  return (
    <PathSelectionPageView
      paths={paths}
      selectedPathId={selectedPathId}
      setSelectedPathId={setSelectedPathId}
      handleContinue={selection.handleContinue}
      status={selection.status}
      mediaImageUrl={mediaImageUrl}
      mediaAltText={(media, fallback) => mediaSummary(media)?.alt_text ?? fallback}
      SectionHeader={SectionHeader}
    />
  );
}

function GoodbyePage() {
  return (
    <section className="celebration-page">
      <h1>See you soon.</h1>
      <p>Your progress is ready for the next learning session.</p>
      <Link className="button button-primary" to="/kids/login">
        Return to Login
      </Link>
    </section>
  );
}

function KidDashboard() {
  const catalog = useCatalogPreview();
  const continueTitle = catalog?.articles[0]?.title ?? 'Surah Al-Fatihah key facts';
  const recommendedQuiz = catalog?.featuredQuiz?.title ?? 'Surah Names';

  return (
    <section>
      <DashboardHeader title="Assalamu Alaikum, Maryam" text="Your Quran learning journey is ready for today." />
      <StatsGrid stats={[['Level', '3'], ['Stars', '450'], ['Streak', '7 days'], ['Badges', '8']]} />
      <section className="daily-mission">
        <div>
          <p className="eyebrow">Today’s mission</p>
          <h2>Complete one short lesson and one quiz.</h2>
          <p>10 minutes is enough. Read, answer, review, and celebrate what you learned.</p>
        </div>
        <div className="progress-ring">65%</div>
      </section>
      <div className="dashboard-grid">
        <ActivityCard title="Continue Learning" text={continueTitle} to="/kids/app/surahs/1" tone="green" />
        <ActivityCard title="Recommended Quiz" text={`${recommendedQuiz}, stars available`} to="/kids/app/quizzes/surah-names" tone="blue" />
        <ActivityCard title="Notifications" text="Learning reminders and parent updates appear here." to="/kids/app/notifications" tone="yellow" />
      </div>
      <section className="weekly-strip">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
          <span className={index < 5 ? 'done' : ''} key={`${day}-${index}`}>
            {day}
          </span>
        ))}
      </section>
    </section>
  );
}

function LearnHome() {
  const catalog = useCatalogPreview();
  const moduleCards = catalog?.modules.length
    ? catalog.modules.map((module) => ({
        title: module.title,
        text: module.description ?? 'A learning space for Quran practice.',
        status: module.status,
        icon: module.icon_key ?? module.title.slice(0, 2),
      }))
    : [
        { title: 'Surah Explorer', text: 'Continue your journey through the Surahs.', status: 'active', icon: 'SE' },
        { title: 'Stories', text: 'Read beautiful stories of prophets and companions.', status: 'active', icon: 'ST' },
      ];
  const articleCards = catalog?.articles.length
    ? catalog.articles.map((article) => {
        const cover = mediaSummary(article.cover_media);
        return {
          title: article.title,
          text: article.summary ?? 'Continue this learning article.',
          imageUrl: mediaImageUrl(article.cover_media),
          imageAlt: cover?.alt_text ?? article.title,
        };
      })
    : learningCards;
  const lessons = catalog?.lessons ?? [];

  return (
    <section>
      <DashboardHeader title="Knowledge Base" text="Search, explore, and continue where you left off." />
      <input className="search-input" placeholder="Search Surahs, meanings, duas, and stories" />
      <div className="module-grid">
        {moduleCards.map((module) => (
          <article className={module.status === 'active' ? 'module-card' : 'module-card locked'} key={module.title}>
            <span>{module.status === 'active' ? module.icon : module.status.replace(/_/g, ' ')}</span>
            <strong>{module.title}</strong>
            <p>{module.text}</p>
          </article>
        ))}
      </div>
      {lessons.length ? (
        <section className="lesson-panel">
          <SectionHeader eyebrow="Lessons" title="Short lessons ready now." text="Pick a small lesson before trying a quiz." />
          <div className="lesson-card-grid">
            {lessons.map((lesson) => (
              <Link className="lesson-card" key={lesson.slug} to={`/kids/app/lessons/${lesson.slug}`}>
                <span>{lesson.surah_id ? `Surah ${lesson.surah_id}` : 'Lesson'}</span>
                <strong>{lesson.title}</strong>
                <p>{lesson.summary ?? 'A short Quran learning step.'}</p>
              </Link>
            ))}
          </div>
        </section>
      ) : null}
      <CardGrid items={articleCards} />
      <section className="continue-panel">
        <div>
          <p className="eyebrow">Continue where you left off</p>
          <h2>Important words from Al-Fatihah</h2>
          <p>Review praise, guidance, mercy, and worship with child-friendly explanations.</p>
        </div>
        <Link className="button button-primary" to="/kids/app/surahs/1">
          Continue
        </Link>
      </section>
    </section>
  );
}

function LessonDetailPage() {
  const { lessonSlug = '' } = useParams();
  const [lesson, setLesson] = useState<KidsLesson | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'missing' | 'saving' | 'saved' | 'fallback'>('loading');

  useEffect(() => {
    let mounted = true;
    loadLessonBySlug(lessonSlug)
      .then((nextLesson) => {
        if (!mounted) return;
        setLesson(nextLesson);
        setStatus(nextLesson ? 'ready' : 'missing');
      })
      .catch(() => {
        if (mounted) setStatus('missing');
      });

    return () => {
      mounted = false;
    };
  }, [lessonSlug]);

  async function handleComplete() {
    if (!lesson) return;
    const profileId = await resolveActiveProfileId();
    if (!profileId) {
      setStatus('fallback');
      return;
    }

    setStatus('saving');
    try {
      await saveLessonProgress({
        profile_id: profileId,
        lesson_id: lesson.id,
        status: 'completed',
        progress_percent: 100,
      });
      setStatus('saved');
    } catch {
      setStatus('fallback');
    }
  }

  if (status === 'loading') return <LoadingPanel title="Loading lesson..." />;

  if (!lesson) {
    return (
      <section className="page-frame">
        <SectionHeader eyebrow="Lesson" title="Lesson not found." text="This lesson may be inactive or unavailable." />
        <Link className="button button-primary" to="/kids/app/learn">Back to Knowledge Base</Link>
      </section>
    );
  }

  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Lesson" title={lesson.title} text={lesson.summary ?? 'A short Quran learning step.'} />
      <section className="split-panel">
        <div>
          <p className="eyebrow">Lesson focus</p>
          <h2>{lesson.surah_id ? `Surah ${lesson.surah_id}` : 'Quran learning'}</h2>
          <p>
            {lesson.ayah_start && lesson.ayah_end
              ? `Practice ayahs ${lesson.ayah_start}-${lesson.ayah_end} with a parent or teacher.`
              : 'Read the summary, discuss the meaning, and continue with a related quiz.'}
          </p>
        </div>
        <div className="lesson-detail-card">
          <strong>What to do</strong>
          <ul className="lesson-list">
            <li>Read the lesson summary out loud.</li>
            <li>Ask what one important word means.</li>
            <li>Mark the lesson complete when you reviewed it.</li>
          </ul>
        </div>
      </section>
      <div className="button-row">
        <button className="button button-primary" disabled={status === 'saving'} onClick={handleComplete} type="button">
          {status === 'saving' ? 'Saving...' : status === 'saved' ? 'Completed' : 'Mark Complete'}
        </button>
        <Link className="button button-secondary" to="/kids/app/learn">Back to Knowledge Base</Link>
      </div>
      {status === 'saved' ? <p className="form-success">Lesson progress saved.</p> : null}
      {status === 'fallback' ? <p className="form-note">Lesson opened. Sign in and choose a child profile to save progress.</p> : null}
    </section>
  );
}

function SurahExplorer() {
  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Surah Explorer"
        title="Explore Quran chapters step by step."
        text="Search, filter, and continue Surah learning with clear progress."
      />
      <div className="filter-row">
        <button>All Surahs</button>
        <button>Short Surahs</button>
        <button>In Progress</button>
        <button>Completed</button>
      </div>
      <div className="surah-grid">
        {surahCards.map(([number, name, arabic, ayahs, progress, summary]) => (
          <Link className="surah-card" key={number} to={`/kids/app/surahs/${number}`}>
            <span>{number}</span>
            <strong>{name}</strong>
            <em>{arabic}</em>
            <p>{summary}</p>
            <small>
              {ayahs} · {progress} complete
            </small>
          </Link>
        ))}
      </div>
    </section>
  );
}

function SurahDetail() {
  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Surah Detail"
        title="Surah Al-Fatihah"
        text="The Opening. A short child-friendly summary, key facts, important words, and related quiz actions will load here."
      />
      <div className="split-panel">
        <div className="arabic-card">الفاتحة</div>
        <div>
          <h2>Key facts</h2>
          <p>7 ayahs. Revealed in Makkah. Recited in every prayer.</p>
          <ul className="lesson-list">
            <li>Meaning: The Opening.</li>
            <li>Important words: praise, mercy, guidance, worship.</li>
            <li>Parent tip: ask your child what guidance means before the quiz.</li>
          </ul>
          <Link className="button button-primary" to="/kids/app/quizzes/surah-names-starter">
            Start Related Quiz
          </Link>
        </div>
      </div>
      <section className="word-grid">
        {[
          ['الحمد', 'Praise'],
          ['الرحمن', 'The Most Merciful'],
          ['اهدنا', 'Guide us'],
        ].map(([arabic, meaning]) => (
          <article key={arabic}>
            <strong>{arabic}</strong>
            <span>{meaning}</span>
          </article>
        ))}
      </section>
    </section>
  );
}

function QuizzesHome() {
  const quizzes = usePublishedQuizzesPreview();
  const visibleQuizzes: QuizCardItem[] = quizzes.length
    ? quizzes.map((quiz) => ({
        imageUrl: mediaImageUrl(quiz.cover_media),
        imageAlt: mediaSummary(quiz.cover_media)?.alt_text ?? quiz.title,
        title: quiz.title,
        slug: quiz.slug,
        category: 'Published quiz',
        difficulty: quiz.difficulty,
        count: `${quiz.question_count} questions`,
        stars: `${quiz.question_count * quiz.stars_per_correct} stars`,
      }))
    : quizCards.map(([title, category, difficulty, count, stars]) => ({
        title,
        slug: slugify(title),
        category,
        difficulty,
        count,
        stars,
      }));

  return (
    <section>
      <DashboardHeader title="Quizzes" text="Choose a Quran quiz and earn stars." />
      <div className="filter-row">
        <button>All</button>
        <button>Easy</button>
        <button>Medium</button>
        <button>Hard</button>
      </div>
      <div className="quiz-grid">
        {visibleQuizzes.map((quiz) => (
          <Link className="quiz-card" key={quiz.slug} to={`/kids/app/quizzes/${quiz.slug}`}>
            {quiz.imageUrl ? <img className="quiz-card-media" src={quiz.imageUrl} alt={quiz.imageAlt} /> : null}
            <span>{quiz.category}</span>
            <h3>{quiz.title}</h3>
            <p>
              {quiz.difficulty} · {quiz.count} · {quiz.stars}
            </p>
          </Link>
        ))}
      </div>
    </section>
  );
}

function useCatalogPreview() {
  const [catalog, setCatalog] = useState<KidsCatalog | null>(null);

  useEffect(() => {
    let mounted = true;

    loadKidsCatalog()
      .then((nextCatalog) => {
        if (mounted) setCatalog(nextCatalog);
      })
      .catch(() => {
        if (mounted) setCatalog(null);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return catalog;
}

function usePublishedQuizzesPreview() {
  const [quizzes, setQuizzes] = useState<KidsQuiz[]>([]);

  useEffect(() => {
    let mounted = true;

    listPublishedQuizzes()
      .then((nextQuizzes) => {
        if (mounted) setQuizzes(nextQuizzes);
      })
      .catch(() => {
        if (mounted) setQuizzes([]);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return quizzes;
}

type AuthFormValues = {
  parentName: string;
  email: string;
  password: string;
};

type AuthFormErrors = Partial<Record<keyof AuthFormValues, string>>;

type ChildAccessRequestValues = {
  childName: string;
  childEmail: string;
  parentEmail: string;
  ageGroupSlug: string;
  message: string;
};

type ChildAccessRequestErrors = Partial<Record<keyof ChildAccessRequestValues, string>>;

function useChildAccessRequestForm() {
  const [values, setValues] = useState<ChildAccessRequestValues>({
    childName: '',
    childEmail: '',
    parentEmail: '',
    ageGroupSlug: 'ages-7-9',
    message: '',
  });
  const [errors, setErrors] = useState<ChildAccessRequestErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  function validate(nextValues: ChildAccessRequestValues) {
    const nextErrors: ChildAccessRequestErrors = {};
    if (!nextValues.childName.trim()) nextErrors.childName = 'Your name is required.';
    if (!/^[^\s@]+@gmail\.com$/i.test(nextValues.childEmail.trim())) nextErrors.childEmail = 'Use a valid Gmail address.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextValues.parentEmail.trim())) nextErrors.parentEmail = 'Enter a valid parent email.';
    if (nextValues.message.trim().length < 20) nextErrors.message = 'Message must be at least 20 characters.';
    return nextErrors;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus('submitting');
    try {
      await submitChildAccessRequest({
        child_name: values.childName.trim(),
        child_email: values.childEmail.trim(),
        parent_email: values.parentEmail.trim(),
        age_group_slug: values.ageGroupSlug,
        message: values.message.trim() || null,
      });
      setStatus('success');
      setValues({ childName: '', childEmail: '', parentEmail: '', ageGroupSlug: 'ages-7-9', message: '' });
    } catch {
      setStatus('error');
    }
  }

  return { errors, handleChange, handleSubmit, status, values };
}

function useAuthForm(mode: 'login' | 'signup') {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState<AuthFormValues>({ parentName: '', email: '', password: '' });
  const [errors, setErrors] = useState<AuthFormErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'magic-link' | 'fallback'>('idle');
  const nextPath = sanitizeKidsNextPath(searchParams.get('next'));

  function validate(nextValues: AuthFormValues) {
    const nextErrors: AuthFormErrors = {};
    if (mode === 'signup' && !nextValues.parentName.trim()) nextErrors.parentName = 'Parent name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextValues.email)) nextErrors.email = 'Enter a valid email address.';
    if (nextValues.password.length < 6) nextErrors.password = 'Password must be at least 6 characters.';
    return nextErrors;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus('submitting');
    try {
      if (mode === 'login') {
        await signInParent({ email: values.email.trim(), password: values.password });
      } else {
        await signUpParent({
          email: values.email.trim(),
          password: values.password,
          parentName: values.parentName.trim(),
        });
      }
      setStatus('success');
      window.setTimeout(() => navigate(nextPath), 450);
    } catch {
      setStatus('fallback');
    }
  }

  async function handleMagicLink() {
    const nextErrors: AuthFormErrors = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) nextErrors.email = 'Enter a valid email address.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus('submitting');
    try {
      await sendParentMagicLink(values.email.trim());
      setStatus('magic-link');
    } catch {
      setStatus('fallback');
    }
  }

  async function handleGoogleSignIn() {
    setStatus('submitting');
    try {
      await signInParentWithGoogle();
    } catch {
      setStatus('fallback');
    }
  }

  return { errors, handleChange, handleGoogleSignIn, handleMagicLink, handleSubmit, status, values };
}

function sanitizeKidsNextPath(nextPath: string | null): string {
  if (!nextPath) return '/kids/onboarding/profile';
  if (!nextPath.startsWith('/kids/')) return '/kids/onboarding/profile';
  return nextPath;
}

type ChildProfileFormValues = {
  displayName: string;
  ageGroup: string;
  avatarKey: string;
  language: string;
  dailyGoal: string;
};

type ChildProfileErrors = Partial<Record<keyof ChildProfileFormValues, string>>;

function useChildProfileForm() {
  const navigate = useNavigate();
  const [ageGroups, setAgeGroups] = useState<KidsAgeGroup[]>([]);
  const [avatars, setAvatars] = useState<KidsAvatar[]>([]);
  const [values, setValues] = useState<ChildProfileFormValues>({
    displayName: '',
    ageGroup: '7-10',
    avatarKey: 'emerald-explorer',
    language: 'en',
    dailyGoal: '10',
  });
  const [errors, setErrors] = useState<ChildProfileErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'fallback'>('idle');

  useEffect(() => {
    let mounted = true;
    listAgeGroups()
      .then((nextAgeGroups) => {
        if (!mounted) return;
        setAgeGroups(nextAgeGroups);
        if (nextAgeGroups.length && !nextAgeGroups.some((ageGroup) => ageGroup.slug === values.ageGroup)) {
          setValues((current) => ({ ...current, ageGroup: nextAgeGroups[0].slug }));
        }
      })
      .catch(() => {
        if (mounted) setAgeGroups([]);
      });

    return () => {
      mounted = false;
    };
  }, [values.ageGroup]);

  useEffect(() => {
    let mounted = true;
    listActiveAvatars()
      .then((nextAvatars) => {
        if (!mounted) return;
        setAvatars(nextAvatars);
        if (nextAvatars.length && !nextAvatars.some((avatar) => avatar.slug === values.avatarKey)) {
          setValues((current) => ({ ...current, avatarKey: nextAvatars[0].slug }));
        }
      })
      .catch(() => {
        if (mounted) setAvatars([]);
      });

    return () => {
      mounted = false;
    };
  }, [values.avatarKey]);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: ChildProfileErrors = {};
    if (!values.displayName.trim()) nextErrors.displayName = 'Child name is required.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setStatus('submitting');
    try {
      const ageGroupId = ageGroups.find((ageGroup) => ageGroup.slug === values.ageGroup)?.id ?? null;
      await saveFamilySettings({
        language: values.language as KidsLanguage,
        daily_goal_minutes: Number(values.dailyGoal),
      });
      const profile = await createChildProfile({
        display_name: values.displayName.trim(),
        age_group_id: ageGroupId,
        avatar_key: values.avatarKey,
      });
      window.localStorage.setItem('alhudah:lastProfileId', profile.id);
      setStatus('success');
      window.setTimeout(() => navigate('/kids/onboarding/path'), 450);
    } catch {
      setStatus('fallback');
    }
  }

  return { ageGroups, avatars, errors, handleChange, handleSubmit, status, values };
}

function useLearningPathSelection(initialPathId: string) {
  const navigate = useNavigate();
  const [selectedPathId, setSelectedPathId] = useState(initialPathId);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'fallback'>('idle');

  useEffect(() => {
    if (!selectedPathId && initialPathId) setSelectedPathId(initialPathId);
  }, [initialPathId, selectedPathId]);

  async function handleContinue() {
    setStatus('submitting');
    try {
      const profileId = await resolveActiveProfileId();
      if (!profileId || !selectedPathId) throw new Error('Profile sync is not ready yet.');
      await selectLearningPath(profileId, selectedPathId);
      navigate('/kids/app');
    } catch {
      setStatus('fallback');
    }
  }

  return { handleContinue, selectedPathId, setSelectedPathId, status };
}

type ParentRequestFormValues = {
  parent_name: string;
  email: string;
  request_type: '' | KidsParentRequestType;
  related_context: string;
  message: string;
};

type ParentRequestErrors = Partial<Record<keyof ParentRequestFormValues, string>>;

function useParentRequestForm() {
  const [values, setValues] = useState<ParentRequestFormValues>({
    parent_name: '',
    email: '',
    request_type: '',
    related_context: '',
    message: '',
  });
  const [errors, setErrors] = useState<ParentRequestErrors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  function validate(nextValues: ParentRequestFormValues) {
    const nextErrors: ParentRequestErrors = {};
    if (!nextValues.parent_name.trim()) nextErrors.parent_name = 'Parent name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextValues.email)) nextErrors.email = 'Enter a valid email address.';
    if (!nextValues.request_type) nextErrors.request_type = 'Choose a request type.';
    if (nextValues.message.trim().length < 20) nextErrors.message = 'Message must be at least 20 characters.';
    return nextErrors;
  }

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setStatus('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !values.request_type) return;

    setStatus('submitting');
    try {
      await submitParentRequest({
        parent_name: values.parent_name.trim(),
        email: values.email.trim(),
        request_type: values.request_type,
        related_context: values.related_context.trim() || null,
        message: values.message.trim(),
      });
      setStatus('success');
      setValues({ parent_name: '', email: '', request_type: '', related_context: '', message: '' });
    } catch {
      setStatus('error');
    }
  }

  return { errors, handleChange, handleSubmit, status, values };
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

type QuizPreviewState =
  | { state: 'loading'; data: null }
  | { state: 'ready'; data: Awaited<ReturnType<typeof loadQuizWithQuestions>> }
  | { state: 'error'; data: null };

type SavedQuizResult = {
  result: KidsQuizAttemptResult;
  question: KidsQuizQuestion & { options: KidsQuizOption[] };
  selectedOption: KidsQuizOption;
};

const quizResultStorageKey = 'alhudah:lastQuizResult';

function useQuizPreview(quizSlug: string): QuizPreviewState {
  const [quiz, setQuiz] = useState<QuizPreviewState>({ state: 'loading', data: null });

  useEffect(() => {
    let mounted = true;
    setQuiz({ state: 'loading', data: null });
    loadQuizWithQuestions(quizSlug)
      .then((data) => {
        if (mounted) setQuiz({ state: 'ready', data });
      })
      .catch(() => {
        if (mounted) setQuiz({ state: 'error', data: null });
      });

    return () => {
      mounted = false;
    };
  }, [quizSlug]);

  return quiz;
}

function saveQuizResult(
  result: KidsQuizAttemptResult,
  question: KidsQuizQuestion & { options: KidsQuizOption[] },
  selectedOption: KidsQuizOption,
) {
  window.localStorage.setItem(quizResultStorageKey, JSON.stringify({ result, question, selectedOption }));
}

function readQuizResult(): SavedQuizResult | null {
  try {
    const raw = window.localStorage.getItem(quizResultStorageKey);
    return raw ? (JSON.parse(raw) as SavedQuizResult) : null;
  } catch {
    return null;
  }
}

async function resolveActiveProfileId() {
  const profiles = await listChildProfiles();
  const firstProfile = profiles[0];
  if (!firstProfile) return null;

  const storedProfileId = window.localStorage.getItem('alhudah:lastProfileId');
  const matchedProfile = storedProfileId ? profiles.find((profile) => profile.id === storedProfileId) : null;
  const resolvedProfile = matchedProfile ?? firstProfile;

  window.localStorage.setItem('alhudah:lastProfileId', resolvedProfile.id);
  return resolvedProfile.id;
}

function LoadingPanel({ title }: { title: string }) {
  return (
    <section className="quiz-stage">
      <p className="eyebrow">Loading</p>
      <h1>{title}</h1>
      <p>Preparing the Quran learning activity.</p>
    </section>
  );
}

function EmptyPanel({ title, text, to }: { title: string; text: string; to: string }) {
  return (
    <section className="quiz-stage">
      <p className="eyebrow">Not ready</p>
      <h1>{title}</h1>
      <p>{text}</p>
      <Link className="button button-primary" to={to}>
        Go Back
      </Link>
    </section>
  );
}

function QuizStart() {
  const { quizSlug = 'surah-names-starter' } = useParams();
  const quiz = useQuizPreview(quizSlug);

  if (quiz.state === 'loading') return <LoadingPanel title="Loading quiz..." />;
  if (!quiz.data) return <EmptyPanel title="Quiz not available" text="Choose another Quran quiz from the quiz list." to="/kids/app/quizzes" />;

  const starsAvailable = quiz.data.questions.length * quiz.data.stars_per_correct;

  return (
    <section className="quiz-stage">
      <p className="eyebrow">{quiz.data.difficulty} quiz</p>
      <h1>{quiz.data.title}</h1>
      <p>{quiz.data.description ?? 'Answer each question, review the explanation, and earn stars for correct answers.'}</p>
      <div className="quiz-summary">
        <span>{quiz.data.difficulty}</span>
        <span>{quiz.data.questions.length} questions</span>
        <span>{starsAvailable} stars</span>
      </div>
      <Link className="button button-primary" to={`/kids/app/quizzes/${quiz.data.slug}/question`}>
        Start Quiz
      </Link>
    </section>
  );
}

function QuizQuestion() {
  const { quizSlug = 'surah-names-starter' } = useParams();
  const navigate = useNavigate();
  const quiz = useQuizPreview(quizSlug);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');

  if (quiz.state === 'loading') return <LoadingPanel title="Loading question..." />;
  if (!quiz.data) return <EmptyPanel title="Quiz not available" text="Choose another Quran quiz from the quiz list." to="/kids/app/quizzes" />;

  const question = quiz.data.questions[0];
  if (!question) return <EmptyPanel title="No questions yet" text="This quiz is being prepared." to="/kids/app/quizzes" />;

  async function handleAnswer(option: KidsQuizOption) {
    const profileId = await resolveActiveProfileId();
    if (!profileId) {
      setStatus('error');
      return;
    }

    setStatus('submitting');
    try {
      const result = await completeQuizAttempt({
        profileId,
        quizSlug: quiz.data!.slug,
        selectedOptionIds: [option.id],
      });
      saveQuizResult(result, question, option);
      navigate(`/kids/app/quizzes/${quiz.data!.slug}/${option.is_correct ? 'correct' : 'keep-going'}`);
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="quiz-stage">
      <div className="progress-track">
        <span style={{ width: `${Math.max(10, 100 / quiz.data.questions.length)}%` }} />
      </div>
      <p className="eyebrow">Question 1 of {quiz.data.questions.length}</p>
      <h1>{question.prompt}</h1>
      {question.hint ? <p className="hint-box">Hint: {question.hint}</p> : null}
      <div className="answer-grid">
        {question.options.map((option) => (
          <button disabled={status === 'submitting'} key={option.id} onClick={() => handleAnswer(option)} type="button">
            {option.option_label}. {option.option_text}
          </button>
        ))}
      </div>
      {status === 'error' ? (
        <p className="form-note">Please finish parent sign-in and child profile setup before taking a quiz.</p>
      ) : null}
    </section>
  );
}

function CorrectFeedback() {
  const { quizSlug = 'surah-names-starter' } = useParams();
  const saved = readQuizResult();
  return (
    <FeedbackPage
      title="MashaAllah! You got it."
      text={saved?.question.explanation ?? 'Great work. Review the explanation and continue.'}
      tone="success"
      quizSlug={quizSlug}
    />
  );
}

function KeepGoingFeedback() {
  const { quizSlug = 'surah-names-starter' } = useParams();
  const saved = readQuizResult();
  const correctOption = saved?.question.options.find((option) => option.is_correct);
  return (
    <FeedbackPage
      title="Keep going."
      text={correctOption ? `The correct answer is ${correctOption.option_text}.` : 'Review the explanation and continue.'}
      tone="soft"
      quizSlug={quizSlug}
    />
  );
}

function FeedbackPage({ title, text, tone, quizSlug }: { title: string; text: string; tone: 'success' | 'soft'; quizSlug: string }) {
  const saved = readQuizResult();

  return (
    <section className={`celebration-page ${tone}`}>
      <h1>{title}</h1>
      <p>{text}</p>
      <div className="feedback-card">
        <strong>Explanation</strong>
        <span>{saved?.question.explanation ?? 'Every answer is a step forward in learning.'}</span>
      </div>
      <Link className="button button-primary" to={`/kids/app/quizzes/${quizSlug}/results`}>
        Continue
      </Link>
    </section>
  );
}

function QuizResults() {
  const { quizSlug = 'surah-names-starter' } = useParams();
  const saved = readQuizResult();
  const result = saved?.result;

  return (
    <section className="page-frame">
      <SectionHeader
        eyebrow="Quiz Results"
        title={result ? `You earned ${result.stars_earned} stars.` : 'Quiz results'}
        text="Review answers, celebrate progress, or continue learning."
      />
      <StatsGrid
        stats={[
          ['Score', result ? `${result.correct_count}/${result.total_questions}` : '-'],
          ['XP', result ? `+${result.xp_earned}` : '-'],
          ['Stars', result ? `+${result.stars_earned}` : '-'],
          ['Badge', result?.correct_count ? 'Updated' : 'Keep trying'],
        ]}
      />
      <section className="result-review">
        <h2>What you learned</h2>
        <p>{saved?.question.fun_fact ?? saved?.question.explanation ?? 'Keep practicing and return for another quiz.'}</p>
      </section>
      <div className="button-row">
        <Link className="button button-secondary" to={`/kids/app/quizzes/${quizSlug}/question`}>Try Again</Link>
        <Link className="button button-primary" to="/kids/app/learn">Continue Learning</Link>
      </div>
    </section>
  );
}

function NotificationsPage() {
  const [items, setItems] = useState<KidsNotification[]>([]);
  const [state, setState] = useState<'loading' | 'synced' | 'empty' | 'fallback'>('loading');

  useEffect(() => {
    let mounted = true;
    resolveActiveProfileId()
      .then((profileId) => {
        if (!profileId) {
          if (mounted) setState('fallback');
          return;
        }
        return listChildNotifications(profileId).then((notifications) => {
          if (!mounted) return;
          setItems(notifications);
          setState(notifications.length ? 'synced' : 'empty');
        });
      })
      .catch(() => {
        if (!mounted) return;
        setState('fallback');
      });

    return () => {
      mounted = false;
    };
  }, []);

  async function handleMarkRead(notification: KidsNotification) {
    if (notification.read_at) return;
    try {
      const nextNotification = await markNotificationRead(notification.id);
      setItems((current) => current.map((item) => (item.id === nextNotification.id ? nextNotification : item)));
    } catch {
      setState('fallback');
    }
  }

  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Notifications" title="Learning reminders and updates." text="A child-safe inbox for progress nudges, rewards, and parent-guided reminders." />
      {state === 'loading' ? <p className="form-note">Loading notifications...</p> : null}
      {state === 'fallback' ? <p className="form-note">Choose a child profile after signing in to sync notifications.</p> : null}
      {state === 'empty' ? <p className="form-success">No notifications right now. Keep learning and check back later.</p> : null}
      <div className="notification-list">
        {items.map((notification) => (
          <article className={notification.read_at ? 'notification-card read' : 'notification-card'} key={notification.id}>
            <span>{notification.type}</span>
            <strong>{notification.title}</strong>
            <p>{notification.body}</p>
            <small>{new Date(notification.created_at).toLocaleDateString()}</small>
            {!notification.read_at ? (
              <button className="button button-secondary" onClick={() => handleMarkRead(notification)} type="button">
                Mark Read
              </button>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

function RewardsPage() {
  const catalog = useCatalogPreview();
  const badges: BadgeCardItem[] = catalog?.badges.length
    ? catalog.badges.map((badge) => {
        const badgeMedia = mediaSummary(badge.media);
        return {
          title: badge.title,
          text: badge.description,
          status: 'Active',
          imageUrl: mediaImageUrl(badge.media),
          imageAlt: badgeMedia?.alt_text ?? badge.title,
        };
      })
    : rewardCards.map(([title, text, status]) => ({ title, text, status }));
  const rewardItems = catalog?.rewardItems ?? [];

  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Rewards" title="Badges, stars, and level progress." text="Celebrate consistency, effort, and learning milestones." />
      <StatsGrid stats={[['Total Stars', '450'], ['Current Level', '3'], ['Next Level', '70%'], ['Badges', '8']]}/>
      <div className="badge-grid">
        {badges.map((badge) => (
          <article className={badge.status === 'Locked' ? 'badge-card locked' : 'badge-card'} key={badge.title}>
            {badge.imageUrl ? <img className="badge-media" src={badge.imageUrl} alt={badge.imageAlt} /> : null}
            <span>{badge.status}</span>
            <strong>{badge.title}</strong>
            <p>{badge.text}</p>
          </article>
        ))}
      </div>
      <section className="reward-shop">
        <SectionHeader eyebrow="Reward Shop" title="Stars can unlock cheerful extras." text="Admins manage these reward items from the CMS." />
        <div className="badge-grid">
          {rewardItems.length ? rewardItems.map((item) => {
            const itemMedia = mediaSummary(item.media);
            return (
              <article className="badge-card" key={item.slug}>
                {mediaImageUrl(item.media) ? <img className="badge-media" src={mediaImageUrl(item.media)} alt={itemMedia?.alt_text ?? item.title} /> : null}
                <span>{item.cost_stars} stars</span>
                <strong>{item.title}</strong>
                <p>{item.description ?? 'A reward item for consistent Quran learning.'}</p>
              </article>
            );
          }) : rewardCards.slice(0, 2).map(([title, text, status]) => (
            <article className={status === 'Locked' ? 'badge-card locked' : 'badge-card'} key={title}>
              <span>{status}</span>
              <strong>{title}</strong>
              <p>{text}</p>
            </article>
          ))}
        </div>
      </section>
    </section>
  );
}

function StreaksPage() {
  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Streaks & Goals" title="Small daily steps build confidence." text="Short sessions can become a joyful family habit." />
      <div className="calendar-card">
        {Array.from({ length: 30 }, (_, index) => (
          <span className={index < 18 ? 'done' : ''} key={index}>
            {index + 1}
          </span>
        ))}
      </div>
      <CardGrid items={[['Daily goal', '10 minutes of Quran learning.'], ['Weekly progress', '5 out of 7 days completed.'], ['Monthly habit', '18 learning days this month.']]} />
    </section>
  );
}

function LeaderboardPage() {
  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Hall of Stars" title="Privacy-safe leaderboard." text="Children appear by avatar and nickname only." />
      <div className="filter-row">
        <button>Friends</button>
        <button>Class</button>
        <button>Global Safe</button>
      </div>
      <div className="leaderboard-card">
        {leaderboardRows.map(([rank, name, stars, avatar]) => (
          <article className={name === 'Maryam' ? 'current' : ''} key={rank}>
            <span>{rank}</span>
            <strong>{name}</strong>
            <small>{avatar}</small>
            <em>{stars}</em>
          </article>
        ))}
      </div>
    </section>
  );
}

function KidProfilePage() {
  const catalog = useCatalogPreview();
  const avatar = catalog?.avatars.find((nextAvatar) => nextAvatar.slug === 'emerald-explorer') ?? null;
  const avatarUrl = mediaImageUrl(avatar?.media);

  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Explorer Profile" title="Maryam’s learning profile." text="A child-safe profile with parent-managed settings." />
      <div className="profile-panel">
        <div className="profile-avatar-large">
          {avatarUrl ? <img src={avatarUrl} alt={mediaSummary(avatar?.media)?.alt_text ?? 'Ayah Reader avatar'} /> : 'M'}
        </div>
        <div>
          <h2>Maryam</h2>
          <p>Age group 7-10 · English with Arabic support · Ayah Reader avatar</p>
          <StatsGrid stats={[['Level', '3'], ['Stars', '450'], ['Badges', '8'], ['Longest streak', '12 days']]} />
          <div className="button-row">
            <Link className="button button-secondary" to="/kids/app/switch">Switch Child</Link>
            <Link className="button button-primary" to="/kids/goodbye">Logout</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SwitchProfilePage() {
  return (
    <section className="page-frame">
      <SectionHeader eyebrow="Switch Explorer" title="Who is learning today?" text="Choose a child profile or ask a parent to add another explorer." />
      <div className="dashboard-grid">
        <ActivityCard title="Maryam" text="Level 3 · 450 stars" to="/kids/app" tone="green" />
        <ActivityCard title="Huda" text="Level 1 · 80 stars" to="/kids/app" tone="blue" />
        <ActivityCard title="Add child" text="Parent confirmation required" to="/kids/parent/children" tone="yellow" />
      </div>
      <section className="parent-lock">
        <h2>Parent area lock</h2>
        <p>Adding children or changing settings requires parent confirmation.</p>
      </section>
    </section>
  );
}

function useParentProgress() {
  const session = useSession();
  const [summary, setSummary] = useState<ParentProgressSummary | null>(null);
  const [state, setState] = useState<'loading' | 'synced' | 'local'>('loading');

  useEffect(() => {
    if (session.isLoading) {
      setState('loading');
      return;
    }
    if (!session.data?.user) {
      setSummary(null);
      setState('local');
      return;
    }

    let mounted = true;
    setState('loading');
    loadParentProgressSummary()
      .then((nextSummary) => {
        if (!mounted) return;
        setSummary(nextSummary);
        setState('synced');
      })
      .catch(() => {
        if (mounted) setState('local');
      });

    return () => {
      mounted = false;
    };
  }, [session.isLoading, session.data?.user?.id]);

  return { state, summary };
}

function profileDisplayRows(summary: ParentProgressSummary | null) {
  if (!summary?.profiles.length) return null;

  const settingsGoal = summary.settings?.daily_goal_minutes ?? 10;
  const attemptsByProfile = new Map<string, number>();
  summary.attempts.forEach((attempt) => {
    attemptsByProfile.set(attempt.profile_id, (attemptsByProfile.get(attempt.profile_id) ?? 0) + 1);
  });

  return summary.profiles.map((profile) => [
    profile.id,
    profile.display_name,
    profile.age_group_id ? 'Age group set' : 'Age group open',
    profile.avatar_key ?? 'Avatar open',
    `Level ${profile.level_number}`,
    `${settingsGoal} min daily`,
    attemptsByProfile.has(profile.id) ? 'Active' : 'Ready',
  ]);
}

function ParentDashboard() {
  const { state, summary } = useParentProgress();
  const completedAttempts = summary?.attempts.filter((attempt) => attempt.status === 'completed') ?? [];
  const totalQuestions = completedAttempts.reduce((sum, attempt) => sum + attempt.total_questions, 0);
  const totalCorrect = completedAttempts.reduce((sum, attempt) => sum + attempt.correct_count, 0);
  const activeDays = new Set(summary?.activityDays.map((day) => day.activity_date)).size;
  const completedLessons = summary?.lessonProgress.filter((lesson) => lesson.status === 'completed').length ?? 0;
  const accuracy = totalQuestions ? `${Math.round((totalCorrect / totalQuestions) * 100)}%` : '0%';
  const latestAttempt = completedAttempts[0];
  const firstProfile = summary?.profiles[0];

  return (
    <ParentDashboardView
      state={state}
      stats={[
        ['Children', `${summary?.profiles.length ?? 0}`],
        ['Quizzes', `${completedAttempts.length}`],
        ['Lessons', `${completedLessons}`],
        ['Accuracy', accuracy],
      ]}
      firstProfileId={firstProfile?.id ?? null}
      firstProfileName={firstProfile?.display_name ?? null}
      latestAttemptSummary={
        latestAttempt
          ? {
              title: latestAttempt.quiz?.title ?? 'A quiz',
              correctCount: latestAttempt.correct_count,
              totalQuestions: latestAttempt.total_questions,
              starsEarned: latestAttempt.stars_earned,
            }
          : null
      }
      completedLessons={completedLessons}
      totalQuestions={totalQuestions}
      activeDays={activeDays}
      hasSettings={!!summary?.settings}
      recommendationCards={recommendationCards}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      StatsGrid={StatsGrid}
      ActivityCard={ActivityCard}
    />
  );
}

function ManageChildren() {
  const { state, summary } = useParentProgress();
  const childRequests = useChildAccessRequests();
  const rows = profileDisplayRows(summary);

  return (
    <ManageChildrenView
      state={state}
      rows={rows}
      childRequests={childRequests}
      parentChildrenFallback={parentChildren}
      SectionHeader={SectionHeader}
      SyncNotice={SyncNotice}
    />
  );
}

function useChildAccessRequests() {
  const session = useSession();
  const [requests, setRequests] = useState<KidsChildAccessRequest[]>([]);
  const [state, setState] = useState<'loading' | 'synced' | 'fallback'>('loading');
  const [actionState, setActionState] = useState<'idle' | 'saving'>('idle');
  const [actionMessage, setActionMessage] = useState<string>('');

  function refresh() {
    setState('loading');
    setActionMessage('');
    listChildAccessRequests()
      .then((nextRequests) => {
        setRequests(nextRequests);
        setState('synced');
      })
      .catch(() => {
        setRequests([]);
        setState('fallback');
      });
  }

  useEffect(() => {
    if (session.isLoading) {
      setState('loading');
      return;
    }
    if (!session.data?.user) {
      setRequests([]);
      setState('fallback');
      return;
    }
    refresh();
  }, [session.isLoading, session.data?.user?.id]);

  async function handleApprove(request: KidsChildAccessRequest) {
    setActionState('saving');
    setActionMessage('');
    try {
      const approvedRequest = await approveChildAccessRequest(request);
      setRequests((current) => current.map((item) => (item.id === approvedRequest.id ? approvedRequest : item)));
      setActionMessage(`${approvedRequest.child_name} was approved and added to this parent account.`);
    } catch {
      setState('fallback');
      setActionMessage('We could not approve that request right now. Please refresh and try again.');
    } finally {
      setActionState('idle');
    }
  }

  async function handleDecline(requestId: string) {
    setActionState('saving');
    setActionMessage('');
    try {
      const declinedRequest = await declineChildAccessRequest(requestId);
      setRequests((current) => current.map((item) => (item.id === declinedRequest.id ? declinedRequest : item)));
      setActionMessage(`${declinedRequest.child_name} was declined.`);
    } catch {
      setState('fallback');
      setActionMessage('We could not decline that request right now. Please refresh and try again.');
    } finally {
      setActionState('idle');
    }
  }

  return { actionMessage, actionState, handleApprove, handleDecline, requests, state };
}

function ChildProgress() {
  const { profileId = '' } = useParams();
  const [data, setData] = useState<Awaited<ReturnType<typeof loadChildProgress>> | null>(null);
  const [state, setState] = useState<'loading' | 'synced' | 'local'>('loading');

  useEffect(() => {
    let mounted = true;
    loadChildProgress(profileId)
      .then((nextData) => {
        if (!mounted) return;
        setData(nextData);
        setState('synced');
      })
      .catch(() => {
        if (mounted) setState('local');
      });

    return () => {
      mounted = false;
    };
  }, [profileId]);

  const profile = data?.profile;
  const attempts = data?.attempts ?? [];
  const lessonProgress = data?.lessonProgress ?? [];
  const completedAttempts = attempts.filter((attempt) => attempt.status === 'completed');
  const completedLessons = lessonProgress.filter((lesson) => lesson.status === 'completed');
  const totalQuestions = completedAttempts.reduce((sum, attempt) => sum + attempt.total_questions, 0);
  const totalCorrect = completedAttempts.reduce((sum, attempt) => sum + attempt.correct_count, 0);
  const accuracy = totalQuestions ? `${Math.round((totalCorrect / totalQuestions) * 100)}%` : '0%';
  const latestAttempt = completedAttempts[0];
  const tableRows = completedAttempts.length
    ? completedAttempts.map((attempt) => [
        attempt.quiz?.title ?? 'Quran Quiz',
        `${attempt.correct_count}/${attempt.total_questions}`,
        `${attempt.stars_earned} stars`,
        attempt.completed_at ? new Date(attempt.completed_at).toLocaleDateString() : 'Completed',
      ])
    : progressRows;

  return (
    <ChildProgressView
      state={state}
      profileName={profile?.display_name ?? 'Child'}
      levelNumber={profile?.level_number ?? 1}
      totalStars={profile?.total_stars ?? 0}
      completedLessonsCount={completedLessons.length}
      accuracy={accuracy}
      latestAttemptSummary={latestAttempt ? { title: latestAttempt.quiz?.title ?? 'Latest quiz', starsEarned: latestAttempt.stars_earned } : null}
      latestAttemptXp={latestAttempt?.xp_earned ?? 0}
      xp={profile?.xp ?? 0}
      tableRows={tableRows}
      recommendationCards={recommendationCards}
      recentLessons={completedLessons.slice(0, 4).map((progress) => ({
        id: progress.id,
        title: progress.lesson?.title ?? 'Completed lesson',
        progressPercent: progress.progress_percent,
      }))}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      StatsGrid={StatsGrid}
    />
  );
}

function ParentSettings() {
  const [values, setValues] = useState({
    parent_name: 'Parent',
    email: 'parent@example.com',
    language: 'English',
    notifications: 'Weekly summary',
    leaderboard: 'Avatar and nickname only',
    message: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<'idle' | 'saving' | 'sent' | 'local'>('idle');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!values.parent_name.trim()) nextErrors.parent_name = 'Parent name is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) nextErrors.email = 'A valid email is required.';
    if (values.message.trim() && values.message.trim().length < 20) nextErrors.message = 'Request details must be at least 20 characters.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setStatus('saving');
    if (!values.message.trim()) {
      setStatus('local');
      return;
    }

    try {
      await submitParentRequest({
        parent_name: values.parent_name.trim(),
        email: values.email.trim(),
        request_type: 'other',
        related_context: 'Parent settings',
        message: values.message.trim(),
      });
      setStatus('sent');
    } catch {
      setStatus('local');
    }
  }

  return (
    <ParentSettingsView
      values={values}
      errors={errors}
      status={status}
      onChange={handleChange}
      onSubmit={handleSubmit}
    />
  );
}

function AdminLogin() {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<'magic' | 'password'>('magic');
  const [values, setValues] = useState({ email: '', password: '' });
  const [status, setStatus] = useState<'idle' | 'checking' | 'denied' | 'error' | 'magic-link'>('idle');
  const [error, setError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setStatus('idle');
    setError('');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus('checking');
    setError('');

    try {
      await signInParent({ email: values.email.trim(), password: values.password });
      const isAdmin = await isCurrentUserKidsAdmin();
      if (!isAdmin) {
        await signOut().catch(() => undefined);
        setStatus('denied');
        return;
      }
      navigate('/kids/admin');
    } catch (nextError) {
      setError(getErrorMessage(nextError));
      setStatus('error');
    }
  }

  async function handleGoogleSignIn() {
    setStatus('checking');
    setError('');

    try {
      await signInAdminWithGoogle();
    } catch (nextError) {
      setError(getErrorMessage(nextError));
      setStatus('error');
    }
  }

  async function handleMagicLink() {
    const email = values.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid CMS email address first.');
      setStatus('error');
      return;
    }

    setStatus('checking');
    setError('');

    try {
      await sendAdminMagicLink(email);
      setStatus('magic-link');
    } catch (nextError) {
      setError(getErrorMessage(nextError));
      setStatus('error');
    }
  }

  return (
    <AdminLoginView
      authMode={authMode}
      values={values}
      status={status}
      error={error}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onGoogleSignIn={handleGoogleSignIn}
      onMagicLink={handleMagicLink}
      onSetAuthMode={(mode) => {
        setAuthMode(mode);
        setStatus('idle');
        setError('');
      }}
    />
  );
}

function SyncNotice({ state, label }: { state: AdminLoadState; label: string }) {
  if (state === 'loading') return <p className="form-note">{label} loading from Supabase...</p>;
  if (state === 'synced') return <p className="form-success">{label} loaded from Supabase.</p>;
  return <p className="form-note">{label} using local fallback until Supabase/auth is ready.</p>;
}

function AdminDashboard() {
  const { readiness, rows: quizRows, state: quizState } = useAdminQuizRows();
  const { items: mediaAssets, state: mediaState } = useAdminMediaRows();
  const { rows: articleRows, state: articleState } = useAdminArticleRows();
  const { badges, rows: badgeRows, state: badgeState } = useAdminBadgeRows();
  const { items: rewardItems, rows: rewardRows, state: rewardState } = useAdminRewardItemRows();
  const { reports, rows: reportRows, state: reportState } = useAdminReportRows();
  const { requests, rows: moderationRows, state: moderationState } = useAdminModerationRows();
  const { rows: auditRows, state: auditState } = useAdminAuditRows();

  const publishedQuizzes = readiness.filter((quiz) => quiz.publish_status === 'published' && quiz.is_active !== false).length;
  const draftQuizzes = readiness.filter((quiz) => quiz.publish_status !== 'published').length || Math.max(quizRows.length - publishedQuizzes, 0);
  const pendingReviews = moderationRows.filter((row) => !['resolved', 'declined'].includes(row[3]?.toLowerCase() ?? '')).length;
  const mediaCount = mediaAssets.length || (mediaState === 'local' ? 0 : mediaAssets.length);
  const activeBadges = badges.filter((badge) => badge.is_active !== false).length || (badgeState === 'local' ? badgeRows.length : 0);
  const activeRewards = rewardItems.filter((item) => item.is_active !== false).length || (rewardState === 'local' ? rewardRows.length : 0);
  const syncedSections = [quizState, mediaState, articleState, badgeState, rewardState, reportState, moderationState, auditState].filter((state) => state === 'synced').length;
  const loadingSections = [quizState, mediaState, articleState, badgeState, rewardState, reportState, moderationState, auditState].filter((state) => state === 'loading').length;
  const averageAccuracy = reports.length
    ? Math.round(reports.reduce((sum, report) => sum + report.average_accuracy, 0) / reports.length)
    : null;
  const latestActivity = auditRows.slice(0, 4);

  return (
    <section className="cms-dashboard">
      <DashboardHeader title="Kids CMS Dashboard" text="Manage quizzes, media, lessons, rewards, moderation, and content quality." />
      <StatsGrid
        stats={[
          ['Published quizzes', `${publishedQuizzes || (quizState === 'local' ? quizRows.length : 0)}`],
          ['Draft quizzes', `${draftQuizzes}`],
          ['Media assets', `${mediaCount}`],
          ['Parent requests', `${requests.length || (moderationState === 'local' ? moderationRows.length : 0)}`],
          ['Active badges', `${activeBadges}`],
          ['Rewards', `${activeRewards}`],
        ]}
      />
      <section className="admin-command-panel cms-command-panel">
        <div>
          <p className="eyebrow">Publishing workflow</p>
          <h2>Build, review, publish, and improve Quran learning content from one CMS.</h2>
          <p>Manage quizzes, media references, lesson content, rewards, moderation, and reporting from one organized workspace.</p>
        </div>
        <div className="cms-dashboard-actions">
          <Link className="button button-primary" to="/kids/admin/quizzes/new">Create Quiz</Link>
          <Link className="button button-secondary" to="/kids/admin/media">Upload Media</Link>
        </div>
      </section>
      <div className="cms-dashboard-grid">
        <section className="cms-panel cms-health-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Content health</p>
              <h2>CMS readiness</h2>
            </div>
            <span className={syncedSections >= 5 ? 'cms-status-pill success' : loadingSections ? 'cms-status-pill review' : 'cms-status-pill draft'}>
              {loadingSections ? 'Loading' : syncedSections >= 5 ? 'Stable' : 'Fallback'}
            </span>
          </div>
          <div className="cms-health-bars">
            <div>
              <span>Synced sections</span>
              <strong>{syncedSections}/8</strong>
              <progress max="8" value={syncedSections} />
            </div>
            <div>
              <span>Quiz accuracy</span>
              <strong>{averageAccuracy === null ? 'Pending' : `${averageAccuracy}%`}</strong>
              <progress max="100" value={averageAccuracy ?? 0} />
            </div>
            <div>
              <span>Pending review</span>
              <strong>{pendingReviews}</strong>
              <progress max={Math.max(moderationRows.length, 1)} value={pendingReviews} />
            </div>
          </div>
        </section>
        <section className="cms-panel cms-quick-actions">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Quick actions</p>
              <h2>Create and review</h2>
            </div>
          </div>
          <div className="cms-action-grid">
            <Link to="/kids/admin/quizzes/new">Create Quiz</Link>
            <Link to="/kids/admin/articles/new">Create Lesson</Link>
            <Link to="/kids/admin/media">Upload Media</Link>
            <Link to="/kids/admin/reports">Review Reports</Link>
          </div>
        </section>
      </div>
      <div className="cms-dashboard-grid lower">
        <section className="cms-panel cms-activity-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Recent activity</p>
              <h2>Latest CMS changes</h2>
            </div>
            <Link to="/kids/admin/audit">View all</Link>
          </div>
          <div className="cms-activity-list">
            {latestActivity.length ? latestActivity.map(([time, action, entity, summary]) => (
              <article key={`${time}-${action}-${summary}`}>
                <span>{action}</span>
                <div>
                  <strong>{summary}</strong>
                  <p>{entity} - {time}</p>
                </div>
              </article>
            )) : (
              <article>
                <span>Ready</span>
                <div>
                  <strong>Audit entries will appear after CMS changes.</strong>
                  <p>{auditState === 'loading' ? 'Loading audit trail...' : 'No recent audit events found.'}</p>
                </div>
              </article>
            )}
          </div>
        </section>
        <section className="cms-panel cms-inventory-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Inventory</p>
              <h2>Managed content</h2>
            </div>
          </div>
          <div className="cms-inventory-list">
            <Link to="/kids/admin/articles"><strong>{articleRows.length}</strong><span>Learning records</span></Link>
            <Link to="/kids/admin/quizzes"><strong>{quizRows.length}</strong><span>Quiz records</span></Link>
            <Link to="/kids/admin/badges"><strong>{activeBadges}</strong><span>Badge records</span></Link>
            <Link to="/kids/admin/reward-items"><strong>{activeRewards}</strong><span>Reward records</span></Link>
            <Link to="/kids/admin/moderation"><strong>{pendingReviews}</strong><span>Review items</span></Link>
            <Link to="/kids/admin/reports"><strong>{reportRows.length}</strong><span>Report rows</span></Link>
          </div>
        </section>
      </div>
    </section>
  );
}

function QuizCmsList() {
  const { readiness, rows, state, refresh } = useAdminQuizRows();
  const [lifecycleState, setLifecycleState] = useState<AdminSaveState>('idle');
  const [lifecycleError, setLifecycleError] = useState('');
  const readyCount = readiness.filter((quiz) => quiz.status === 'Ready to publish').length;
  const publishableQuizzes = readiness.filter((quiz) => quiz.status === 'Ready to publish' && quiz.publish_status !== 'published');
  const publishedQuizzes = readiness.filter((quiz) => quiz.publish_status === 'published' && quiz.is_active !== false);
  const missingQuestionCount = readiness.reduce((sum, quiz) => sum + quiz.missing_questions, 0);
  const answerIssueCount = readiness.reduce((sum, quiz) => sum + quiz.invalid_answer_questions, 0);
  const explanationIssueCount = readiness.reduce((sum, quiz) => sum + quiz.missing_explanations, 0);

  async function handlePublish(quiz: AdminQuizReadinessRow) {
    setLifecycleState('saving');
    setLifecycleError('');
    try {
      await publishAdminQuiz(quiz.quiz_id);
      refresh();
      setLifecycleState('synced');
    } catch (error) {
      setLifecycleError(getErrorMessage(error));
      setLifecycleState(state === 'local' ? 'local' : 'error');
    }
  }

  async function handleArchive(quiz: AdminQuizReadinessRow) {
    setLifecycleState('saving');
    setLifecycleError('');
    try {
      await archiveAdminQuiz(quiz.quiz_id);
      refresh();
      setLifecycleState('synced');
    } catch (error) {
      setLifecycleError(getErrorMessage(error));
      setLifecycleState(state === 'local' ? 'local' : 'error');
    }
  }

  return (
    <section className="cms-list-page quiz-cms-page">
      <div className="cms-page-heading">
        <div>
          <p className="eyebrow">Quiz CMS</p>
          <h1>Quiz Management</h1>
          <p>Create, review, publish, and archive Quran learning quizzes for the child-facing experience.</p>
        </div>
        <Link className="button button-primary" to="/kids/admin/quizzes/new">
          Create Quiz
        </Link>
      </div>
      <SyncNotice label="Quiz CMS" state={state} />
      <StatsGrid stats={[['Total quizzes', `${readiness.length || rows.length}`], ['Ready', `${readyCount}`], ['Missing questions', `${missingQuestionCount}`], ['Answer issues', `${answerIssueCount}`], ['Explanation gaps', `${explanationIssueCount}`]]} />
      <section className="cms-filter-panel">
        <label>
          Search
          <input disabled placeholder="Search quizzes..." type="search" />
        </label>
        <label>
          Status
          <select disabled>
            <option>All statuses</option>
            <option>Draft</option>
            <option>Published</option>
            <option>Archived</option>
          </select>
        </label>
        <label>
          Category
          <select disabled>
            <option>All categories</option>
          </select>
        </label>
        <span>Filters are visual in Phase 4; live filtering comes after question bank data wiring.</span>
      </section>
      <section className="admin-command-panel cms-command-panel">
        <div>
          <p className="eyebrow">Lifecycle control</p>
          <h2>{publishableQuizzes.length ? 'Ready drafts can be published now.' : publishedQuizzes.length ? 'Published quizzes can be archived safely.' : 'No lifecycle actions are ready yet.'}</h2>
          <p>Publishing and archiving are guarded by Supabase. Archiving removes a quiz from the child-facing experience without deleting content.</p>
          <SaveNotice error={lifecycleError} state={lifecycleState} />
        </div>
        <div className="button-row">
          {publishableQuizzes.slice(0, 2).map((quiz) => (
            <button className="button button-primary" disabled={lifecycleState === 'saving'} key={`publish-${quiz.quiz_id}`} onClick={() => handlePublish(quiz)} type="button">
              {lifecycleState === 'saving' ? 'Working...' : `Publish ${quiz.quiz_title}`}
            </button>
          ))}
          {publishedQuizzes.slice(0, 2).map((quiz) => (
            <button className="button button-secondary" disabled={lifecycleState === 'saving'} key={`archive-${quiz.quiz_id}`} onClick={() => handleArchive(quiz)} type="button">
              {lifecycleState === 'saving' ? 'Working...' : `Archive ${quiz.quiz_title}`}
            </button>
          ))}
          {!publishableQuizzes.length && !publishedQuizzes.length ? <Link className="button button-secondary" to="/kids/admin/questions">Add Questions</Link> : null}
        </div>
      </section>
      <section className="cms-panel cms-readiness-table">
        <div className="cms-panel-heading">
          <div>
            <p className="eyebrow">Readiness</p>
            <h2>Quiz records</h2>
          </div>
          <Link to="/kids/admin/quiz-preview">Preview quiz</Link>
        </div>
        <div className="cms-table">
          <div className="cms-table-row header">
            <span>Quiz</span>
            <span>Category</span>
            <span>Questions</span>
            <span>Publish</span>
            <span>Readiness</span>
            <span>Issues</span>
          </div>
          {(readiness.length ? readiness : rows.map((row, index) => ({
            quiz_id: `fallback-${index}`,
            quiz_title: row[0],
            quiz_slug: toSlug(row[0]),
            category_title: row[1] ?? 'General',
            actual_questions: Number.parseInt(row[2] ?? '0', 10) || 0,
            declared_questions: Number.parseInt(row[2] ?? '10', 10) || 10,
            publish_status: (row[3] ?? 'draft') as AdminQuizReadinessRow['publish_status'],
            status: (row[4] ?? 'Needs questions') as AdminQuizReadinessRow['status'],
            missing_questions: 0,
            invalid_answer_questions: 0,
            missing_explanations: 0,
            is_active: true,
          }))).map((quiz) => {
            const issueCount = quiz.missing_questions + quiz.invalid_answer_questions + quiz.missing_explanations;
            return (
              <div className="cms-table-row" key={quiz.quiz_id}>
                <span><strong>{quiz.quiz_title}</strong><small>{quiz.quiz_slug}</small></span>
                <span>{quiz.category_title}</span>
                <span>{quiz.actual_questions}/{quiz.declared_questions}</span>
                <span><StatusBadge value={quiz.publish_status ?? 'draft'} /></span>
                <span><StatusBadge value={quiz.status} /></span>
                <span>{issueCount ? `${issueCount} issue${issueCount === 1 ? '' : 's'}` : 'Clear'}</span>
              </div>
            );
          })}
        </div>
      </section>
    </section>
  );
}

function StatusBadge({ value }: { value: string }) {
  const normalized = value.toLowerCase().replace(/_/g, ' ');
  const tone = normalized.includes('published') || normalized.includes('ready')
    ? 'success'
    : normalized.includes('review') || normalized.includes('explanation')
      ? 'review'
      : normalized.includes('archive') || normalized.includes('fix')
        ? 'archived'
        : 'draft';

  return <span className={`cms-status-pill ${tone}`}>{normalized}</span>;
}

function CreateEditQuiz() {
  const { categories, state: categoryState } = useAdminCategories();
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    title: 'Surah Names Quiz',
    slug: 'surah-names-quiz',
    description: 'Practice Quran chapter names with child-friendly explanations.',
    category_id: '',
    difficulty: 'easy',
    age_group_id: '',
    language: 'en',
    estimated_minutes: '8',
    stars_per_correct: '10',
    xp_per_correct: '10',
    question_count: '10',
    cover_media_id: '',
    status: 'draft',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const slugPreview = toSlug(values.slug || values.title);
  const selectedCategory = categories.find((category) => category.id === values.category_id) ?? null;
  const selectedCover = mediaAssets.find((asset) => asset.id === values.cover_media_id) ?? null;
  const questionCount = Number(values.question_count) || 0;
  const rewardStars = Number(values.stars_per_correct) || 0;
  const xpReward = Number(values.xp_per_correct) || 0;
  const readinessChecks = [
    { label: 'Quiz title', ready: Boolean(values.title.trim()) },
    { label: 'Category selected', ready: Boolean(values.category_id) },
    { label: 'Valid slug', ready: Boolean(slugPreview) },
    { label: 'Reward configured', ready: rewardStars > 0 && xpReward > 0 },
    { label: 'Question target set', ready: questionCount > 0 },
  ];
  const completedChecks = readinessChecks.filter((check) => check.ready).length;
  const previewOptions = [
    'The Opening',
    'The Sincerity',
    'The Dawn',
  ];

  useEffect(() => {
    if (categories.length && !values.category_id) {
      setValues((current) => ({ ...current, category_id: categories[0].id ?? '' }));
    }
  }, [categories, values.category_id]);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: name === 'title' && !current.slug ? value : value,
      slug: name === 'title' && !current.slug ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const slug = toSlug(values.slug || values.title);
    const questionCount = Number(values.question_count);
    const estimatedMinutes = Number(values.estimated_minutes);
    const stars = Number(values.stars_per_correct);
    const xp = Number(values.xp_per_correct);

    if (!values.title.trim()) nextErrors.title = 'Quiz title is required.';
    if (!slug) nextErrors.slug = 'Slug is required and must contain letters or numbers.';
    if (!values.category_id) nextErrors.category_id = 'Choose a category before syncing.';
    if (!Number.isInteger(questionCount) || questionCount < 1) nextErrors.question_count = 'Question count must be at least 1.';
    if (!Number.isInteger(estimatedMinutes) || estimatedMinutes < 1) nextErrors.estimated_minutes = 'Estimated minutes must be at least 1.';
    if (!Number.isInteger(stars) || stars < 1) nextErrors.stars_per_correct = 'Stars must be at least 1.';
    if (!Number.isInteger(xp) || xp < 1) nextErrors.xp_per_correct = 'XP must be at least 1.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      await saveQuizDraft({
        title: values.title.trim(),
        slug,
        description: values.description.trim() || null,
        category_id: values.category_id,
        age_group_id: values.age_group_id || null,
        difficulty: values.difficulty as 'easy' | 'medium' | 'hard',
        language: values.language as 'en' | 'ar' | 'ur',
        question_count: questionCount,
        stars_per_correct: stars,
        xp_per_correct: xp,
        status: values.status as 'draft' | 'published' | 'archived',
        estimated_minutes: estimatedMinutes,
        cover_media_id: values.cover_media_id || null,
      });
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(categoryState === 'local' ? 'local' : 'error');
    }
  }

  return (
    <section className="cms-builder-page quiz-builder-page">
      <div className="cms-builder-topbar">
        <Link className="cms-back-link" to="/kids/admin/quizzes">Back to Quizzes</Link>
        <div>
          <p className="eyebrow">Quiz Builder</p>
          <h1>Create New Quiz</h1>
          <p>Build the quiz metadata, reward rules, child preview, and publishing readiness before adding question records.</p>
        </div>
        <div className="cms-builder-actions">
          <Link className="button button-secondary" to={`/kids/admin/quiz-preview?quiz=${slugPreview || values.slug}`}>
            Preview
          </Link>
          <button className="button button-primary" disabled={saveState === 'saving'} form="quiz-builder-form" type="submit">
            {saveState === 'saving' ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>

      <div className="cms-builder-layout">
        <form className="cms-builder-form" id="quiz-builder-form" onSubmit={handleSubmit}>
          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>General Information</h2>
                <p>These fields power the quiz cards, child app search, and parent progress reports.</p>
              </div>
            </div>
            <SyncNotice label="Categories" state={categoryState} />
            <div className="form-grid cms-form-grid">
              <label className="wide">Quiz title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
              <label>URL slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
              <label>
                Category
                <select name="category_id" onChange={handleChange} value={values.category_id}>
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id ?? category.slug} value={category.id ?? ''}>{category.title}</option>
                  ))}
                </select>
                {errors.category_id ? <span className="field-error">{errors.category_id}</span> : null}
              </label>
              <label className="wide">Description<textarea name="description" onChange={handleChange} value={values.description} /></label>
            </div>
          </section>

          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>02</span>
              <div>
                <h2>Learning Rules</h2>
                <p>Set level, language, time estimate, and reward values used by the child quiz flow.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label>Difficulty<select name="difficulty" onChange={handleChange} value={values.difficulty}><option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option></select></label>
              <label>Language<select name="language" onChange={handleChange} value={values.language}><option value="en">English</option><option value="ar">Arabic</option><option value="ur">Urdu</option></select></label>
              <label>Question target<input name="question_count" onChange={handleChange} type="number" value={values.question_count} />{errors.question_count ? <span className="field-error">{errors.question_count}</span> : null}</label>
              <label>Estimated minutes<input name="estimated_minutes" onChange={handleChange} type="number" value={values.estimated_minutes} />{errors.estimated_minutes ? <span className="field-error">{errors.estimated_minutes}</span> : null}</label>
              <label>Stars per correct answer<input name="stars_per_correct" onChange={handleChange} type="number" value={values.stars_per_correct} />{errors.stars_per_correct ? <span className="field-error">{errors.stars_per_correct}</span> : null}</label>
              <label>XP per correct answer<input name="xp_per_correct" onChange={handleChange} type="number" value={values.xp_per_correct} />{errors.xp_per_correct ? <span className="field-error">{errors.xp_per_correct}</span> : null}</label>
            </div>
          </section>

          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>03</span>
              <div>
                <h2>Publishing Assets</h2>
                <p>Choose the quiz cover and lifecycle status. Publish only after questions and explanations are complete.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label>Cover image<select name="cover_media_id" onChange={handleChange} value={values.cover_media_id}><option value="">No cover image</option>{mediaAssets.filter((asset) => asset.category === 'quiz_cover' || asset.category === 'website').map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
              <label>Status<select name="status" onChange={handleChange} value={values.status}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <div className="button-row">
              <Link className="button button-secondary" to="/kids/admin/questions">Add Questions</Link>
              <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Draft'}</button>
            </div>
          </section>
        </form>

        <aside className="cms-builder-preview" aria-label="Quiz readiness preview">
          <div className="cms-preview-card">
            <div className="cms-phone-frame">
              <div className="cms-phone-notch" />
              <div className="cms-phone-progress"><span style={{ width: `${Math.max((completedChecks / readinessChecks.length) * 100, 12)}%` }} /></div>
              <div className="cms-phone-cover">
                {selectedCover?.public_url ? <img alt={selectedCover.alt_text ?? selectedCover.filename} src={selectedCover.public_url} /> : <span>{selectedCategory?.title?.slice(0, 2) ?? 'QZ'}</span>}
              </div>
              <h3>{values.title || 'Untitled quiz'}</h3>
              <p>{values.description || 'Add a child-friendly description for this Quran quiz.'}</p>
              <div className="cms-preview-options">
                {previewOptions.map((option, index) => (
                  <span className={index === 0 ? 'selected' : ''} key={option}>{option}</span>
                ))}
              </div>
            </div>
          </div>
          <section className="cms-readiness-card">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Readiness</p>
                <h2>{completedChecks}/{readinessChecks.length} checks complete</h2>
              </div>
              <StatusBadge value={values.status} />
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
            <div className="cms-reward-strip">
              <strong>{rewardStars * Math.max(questionCount, 1)}</strong>
              <span>possible stars</span>
              <strong>{xpReward * Math.max(questionCount, 1)}</strong>
              <span>possible XP</span>
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function QuestionBuilder() {
  const [quizzes, setQuizzes] = useState<KidsQuiz[]>([]);
  const [loadState, setLoadState] = useState<AdminLoadState>('loading');
  const [savedQuestions, setSavedQuestions] = useState<Array<KidsQuizQuestion & { options: KidsQuizOption[] }>>([]);
  const [values, setValues] = useState({
    quiz_id: '',
    prompt: 'Which Surah is called The Opening?',
    arabic_text: 'الفاتحة',
    hint: 'This Surah begins every prayer.',
    explanation: 'Al-Fatihah means The Opening. It teaches praise, worship, and asking Allah for guidance.',
    fun_fact: 'Al-Fatihah is recited in every unit of the prayer.',
    sort_order: '10',
    is_active: 'true',
    answer_a: 'Al-Fatihah',
    answer_b: 'Al-Ikhlas',
    answer_c: 'An-Nas',
    answer_d: 'Al-Falaq',
    correct_answer: 'A',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let isMounted = true;
    listAdminQuizzes()
      .then((items) => {
        if (!isMounted) return;
        setQuizzes(items);
        setLoadState('synced');
        if (items[0]) {
          setValues((current) => ({ ...current, quiz_id: current.quiz_id || items[0].id }));
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setQuizzes([]);
        setLoadState('local');
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const sortOrder = Number(values.sort_order);
    if (!values.quiz_id) nextErrors.quiz_id = 'Choose the quiz this question belongs to.';
    if (values.prompt.trim().length < 10) nextErrors.prompt = 'Question text must be at least 10 characters.';
    if (values.explanation.trim().length < 20) nextErrors.explanation = 'Explanation must be at least 20 characters.';
    if (!Number.isInteger(sortOrder) || sortOrder < 1) nextErrors.sort_order = 'Sort order must be at least 1.';
    (['answer_a', 'answer_b', 'answer_c', 'answer_d'] as const).forEach((key) => {
      if (!values[key].trim()) nextErrors[key] = 'Answer option is required.';
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedQuestion = await saveQuizQuestionDraft({
        quiz_id: values.quiz_id,
        prompt: values.prompt.trim(),
        arabic_text: values.arabic_text.trim() || null,
        hint: values.hint.trim() || null,
        explanation: values.explanation.trim(),
        fun_fact: values.fun_fact.trim() || null,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
        options: ([
          ['A', values.answer_a],
          ['B', values.answer_b],
          ['C', values.answer_c],
          ['D', values.answer_d],
        ] as const).map(([label, text], index) => ({
          option_label: label,
          option_text: text.trim(),
          is_correct: values.correct_answer === label,
          feedback_text: values.correct_answer === label ? 'Correct. Great job!' : 'Try again and review the explanation.',
          sort_order: index + 1,
        })),
      });
      setSavedQuestions((current) => {
        const exists = current.some((question) => question.id === savedQuestion.id);
        return exists
          ? current.map((question) => (question.id === savedQuestion.id ? savedQuestion : question))
          : [savedQuestion, ...current];
      });
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(loadState === 'local' ? 'local' : 'error');
    }
  }

  const selectedQuiz = quizzes.find((quiz) => quiz.id === values.quiz_id) ?? null;
  const previewTo = selectedQuiz ? `/kids/admin/quiz-preview?quiz=${selectedQuiz.slug}` : '/kids/admin/quiz-preview';
  const savedQuestionLabels = savedQuestions.map((question) => `${question.sort_order}. ${question.prompt}`);

  return (
    <QuestionBuilderView
      loadState={loadState}
      values={values}
      errors={errors}
      saveState={saveState}
      saveError={saveError}
      quizOptions={quizzes.map((quiz) => ({ id: quiz.id, title: quiz.title }))}
      previewTo={previewTo}
      savedQuestionLabels={savedQuestionLabels}
      onChange={handleChange}
      onSubmit={handleSubmit}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      SaveNotice={SaveNotice}
    />
  );
}

function QuizPreview() {
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof loadAdminQuizPreview>>>(null);
  const [quizzes, setQuizzes] = useState<KidsQuiz[]>([]);
  const [state, setState] = useState<AdminLoadState>('loading');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSlug = searchParams.get('quiz') ?? '';

  useEffect(() => {
    let isMounted = true;
    Promise.all([listAdminQuizzes(), loadAdminQuizPreview(selectedSlug || undefined)])
      .then(([items, data]) => {
        if (!isMounted) return;
        setQuizzes(items);
        setPreview(data);
        if (!selectedSlug && data?.slug) {
          setSearchParams({ quiz: data.slug }, { replace: true });
        }
        setState('synced');
      })
      .catch(() => {
        if (!isMounted) return;
        setQuizzes([]);
        setPreview(null);
        setState('local');
      });

    return () => {
      isMounted = false;
    };
  }, [selectedSlug, setSearchParams]);

  const firstQuestion = preview?.questions[0] ?? null;
  const correctOption = firstQuestion?.options.find((option) => option.is_correct) ?? null;

  return (
    <section className="quiz-stage admin-preview">
      <p className="eyebrow">Quiz Preview</p>
      <SyncNotice label="Quiz preview" state={state} />
      <label className="preview-selector">
        Preview quiz
        <select
          onChange={(event) => setSearchParams({ quiz: event.target.value })}
          value={preview?.slug ?? selectedSlug}
        >
          {quizzes.length ? null : <option value="">No quizzes ready</option>}
          {quizzes.map((quiz) => (
            <option key={quiz.id} value={quiz.slug}>{quiz.title}</option>
          ))}
        </select>
      </label>
      <h1>{firstQuestion?.prompt ?? preview?.title ?? 'No quiz questions ready for preview.'}</h1>
      <div className="quiz-summary">
        <span>{preview?.title ?? 'Create a quiz first'}</span>
        <span>{preview?.difficulty ?? 'Draft'}</span>
        <span>{preview ? `${preview.stars_per_correct} stars per correct answer` : 'No rewards yet'}</span>
      </div>
      {firstQuestion ? (
        <div className="answer-grid">
          {firstQuestion.options.map((option) => (
            <Link key={option.id} to="/kids/admin/questions">
              {option.option_label}. {option.option_text}
            </Link>
          ))}
        </div>
      ) : (
        <div className="feedback-card">
          <strong>No questions yet</strong>
          <span>Add at least one question before previewing the learner experience.</span>
        </div>
      )}
      <div className="feedback-card">
        <strong>{correctOption ? `Correct answer: ${correctOption.option_label}` : 'Preview note'}</strong>
        <span>{firstQuestion?.explanation ?? 'Preview confirms child wording, answer layout, hint, explanation, and reward display before publishing.'}</span>
      </div>
      <Link className="button button-primary" to="/kids/admin/questions">
        Back to Builder
      </Link>
    </section>
  );
}

function CategoryManagement() {
  const { categories, rows, state } = useAdminCategoryRows();
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    title: 'Surah Practice',
    slug: 'surah-practice',
    description: 'Age-friendly quizzes about Surah names, meanings, and key lessons.',
    icon_key: 'book-open',
    color_key: 'emerald',
    media_id: '',
    sort_order: '40',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Category title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 12) nextErrors.description = 'Description should explain the category clearly.';
    const sortOrder = Number(values.sort_order);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      await saveQuizCategory({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        icon_key: values.icon_key.trim() || null,
        color_key: values.color_key.trim() || null,
        media_id: values.media_id || null,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
      });
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const categoryMedia = mediaAssets.filter((asset) => asset.category === 'website' || asset.category === 'icon' || asset.category === 'quiz_cover');
  const activeCategories = categories.filter((category) => category.is_active !== false).length;
  const inactiveCategories = categories.filter((category) => category.is_active === false).length;
  const imageBackedCategories = categories.filter((category) => Boolean(category.media_id)).length;
  const nextSortOrder = categories.length ? Math.max(...categories.map((category) => category.sort_order)) + 10 : 10;
  const selectedMedia = categoryMedia.find((asset) => asset.id === values.media_id);
  const selectedMediaUrl = selectedMedia ? mediaImageUrl(selectedMedia) : undefined;
  const categoryCards = categories.length
    ? categories
    : rows.map(([title, slug, description, status], index) => ({
        id: `fallback-category-${index}`,
        title,
        slug,
        description,
        icon_key: index === 0 ? 'book-open' : index === 1 ? 'moon' : 'sparkles',
        color_key: index === 0 ? 'emerald' : index === 1 ? 'sky' : 'amber',
        media_id: null,
        sort_order: (index + 1) * 10,
        is_active: status !== 'Inactive',
      } satisfies KidsQuizCategory));
  const readinessChecks = [
    { label: 'Child-friendly title', ready: values.title.trim().length >= 3 },
    { label: 'Clean category slug', ready: /^[a-z0-9-]+$/.test(values.slug) },
    { label: 'Description explains use', ready: values.description.trim().length >= 12 },
    { label: 'Icon or media mapped', ready: Boolean(values.icon_key.trim() || values.media_id) },
    { label: 'Color key set', ready: Boolean(values.color_key.trim()) },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms category-cms-page">
      <DashboardHeader title="Categories CMS" text="Create the quiz and learning categories children see across Quran practice, lessons, and discovery." />
      <SyncNotice label="Categories" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article className="accent"><span>Total categories</span><strong>{categories.length || rows.length}</strong><em>Content groups</em></article>
        <article><span>Active</span><strong>{activeCategories || rows.length}</strong><em>Visible to children</em></article>
        <article><span>Inactive</span><strong>{inactiveCategories}</strong><em>Hidden categories</em></article>
        <article><span>With images</span><strong>{imageBackedCategories}</strong><em>Media mapped</em></article>
        <article><span>Next order</span><strong>{nextSortOrder}</strong><em>Suggested sort</em></article>
      </div>

      <div className="category-cms-layout">
        <section className="cms-panel category-library-panel">
          <div className="cms-panel-heading">
            <div>
              <span>Category Library</span>
              <h2>Learning and quiz groups</h2>
            </div>
            <span>{categoryCards.length} records</span>
          </div>
          <div className="category-card-grid-cms">
            {categoryCards.map((category) => {
              const media = categoryMedia.find((asset) => asset.id === category.media_id);
              const imageUrl = media ? mediaImageUrl(media) : undefined;
              return (
                <article key={category.slug}>
                  <div className="category-card-icon">
                    {imageUrl ? <img src={imageUrl} alt={media?.alt_text ?? category.title} /> : <span>{category.icon_key ?? category.title.slice(0, 1)}</span>}
                  </div>
                  <div>
                    <div className="path-card-heading">
                      <h3>{category.title}</h3>
                      <span className={`status-chip ${category.is_active === false ? 'draft' : 'published'}`}>{category.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                    <p>{category.description ?? 'No description yet.'}</p>
                    <div className="path-card-meta">
                      <span>{category.slug}</span>
                      <span>{category.color_key ?? 'No color key'}</span>
                      <span>Order {category.sort_order}</span>
                      <span>{media?.filename ?? 'No media'}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="category-side-panel">
          <form className="auth-card wide category-editor-card" onSubmit={handleSubmit}>
            <p className="eyebrow">Category Builder</p>
            <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
            <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
            <label>Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
            <label>Icon key<input name="icon_key" onChange={handleChange} value={values.icon_key} /></label>
            <label>Color key<input name="color_key" onChange={handleChange} value={values.color_key} /></label>
            <label>Category image<select name="media_id" onChange={handleChange} value={values.media_id}><option value="">No category image</option>{categoryMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
            <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
            <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Category'}</button>
          </form>

          <section className="category-preview-card">
            <div className="cms-panel-heading">
              <div>
                <span>Child Preview</span>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
            </div>
            <div className="category-child-preview">
              <div className="category-preview-art">
                {selectedMediaUrl ? <img src={selectedMediaUrl} alt={selectedMedia?.alt_text ?? values.title} /> : <span>{values.icon_key || 'book'}</span>}
              </div>
              <strong>{values.title}</strong>
              <p>{values.description}</p>
              <span>{values.color_key || 'No color'} - {values.is_active === 'true' ? 'Visible' : 'Hidden'}</span>
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function LearningPathCms() {
  const { addOrReplacePath, paths, rows, state } = useAdminLearningPathRows();
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    title: 'Quran Words Adventure',
    slug: 'quran-words-adventure',
    description: 'A guided path for children to learn important Quran words with short practice steps.',
    icon_key: 'path_words',
    media_id: '',
    sort_order: '40',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Path title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 16) nextErrors.description = 'Description should explain the path clearly.';
    const sortOrder = Number(values.sort_order);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedPath = await saveLearningPath({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        icon_key: values.icon_key.trim() || null,
        media_id: values.media_id || null,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
      });
      addOrReplacePath(savedPath);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const pathMedia = mediaAssets.filter((asset) =>
    ['learning_article', 'website', 'hero', 'icon', 'other'].includes(asset.category),
  );
  const activePaths = paths.filter((path) => path.is_active !== false).length || rows.filter((row) => row[3] === 'Active').length;
  const inactivePaths = paths.filter((path) => path.is_active === false).length || rows.filter((row) => row[3] === 'Inactive').length;
  const nextOrder = paths.length ? Math.max(...paths.map((path) => path.sort_order)) + 10 : 10;
  const selectedMedia = mediaAssets.find((asset) => asset.id === values.media_id) ?? null;
  const pathCards = paths.length
    ? paths.map((path) => ({
        title: path.title,
        slug: path.slug,
        description: path.description ?? 'No description yet',
        status: path.is_active === false ? 'Inactive' : 'Active',
        order: path.sort_order,
      }))
    : rows.map(([title, slug, description, status], index) => ({
        title,
        slug,
        description,
        status,
        order: (index + 1) * 10,
      }));

  return (
    <section className="learning-paths-cms">
      <DashboardHeader title="Learning Paths" text="Create the guided paths parents choose during child onboarding." />
      <SyncNotice label="Learning paths" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article><span>Total paths</span><strong>{paths.length || rows.length}</strong><em>Available journeys</em></article>
        <article><span>Active</span><strong>{activePaths}</strong><em>Visible in onboarding</em></article>
        <article><span>Inactive</span><strong>{inactivePaths}</strong><em>Hidden paths</em></article>
        <article className="accent"><span>Next order</span><strong>{nextOrder}</strong><em>Suggested sort value</em></article>
      </div>
      <div className="path-cms-layout">
        <section className="cms-panel path-registry-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Pathways Registry</p>
              <h2>Guided learning paths</h2>
            </div>
            <span>{pathCards.length} paths</span>
          </div>
          <div className="path-card-list">
            {pathCards.map((path) => (
              <article key={path.slug}>
                <div className="path-card-media"><span>{path.title.slice(0, 2).toUpperCase()}</span></div>
                <div>
                  <div className="path-card-heading">
                    <h3>{path.title}</h3>
                    <StatusBadge value={path.status} />
                  </div>
                  <p>{path.description}</p>
                  <div className="path-card-meta">
                    <span>{path.slug}</span>
                    <span>Order {path.order}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>
        <aside className="path-side-panel">
          <form className="cms-editor-section path-editor-card" onSubmit={handleSubmit}>
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>Path Editor</h2>
                <p>Create or update the path record used during child onboarding.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label className="wide">Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
              <label className="wide">Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
              <label className="wide">Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
              <label>Icon key<input name="icon_key" onChange={handleChange} value={values.icon_key} /></label>
              <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
              <label>Path image<select name="media_id" onChange={handleChange} value={values.media_id}><option value="">No path image</option>{pathMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
              <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Learning Path'}</button>
          </form>
          <section className="path-order-panel">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Ordering</p>
                <h2>Journey flow</h2>
              </div>
            </div>
            {(paths.length ? paths : pathCards).slice(0, 4).map((path, index) => (
              <div className={index === 0 ? 'active' : ''} key={`order-${path.slug}`}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{path.title}</strong>
              </div>
            ))}
            <p>{selectedMedia ? `Selected media: ${selectedMedia.filename}` : 'No path image selected yet.'}</p>
          </section>
        </aside>
      </div>
    </section>
  );
}

function LearningModuleCms() {
  const { addOrReplaceModule, modules, rows, state } = useAdminLearningModuleRows();
  const [values, setValues] = useState({
    title: 'Quran Words',
    slug: 'quran-words',
    description: 'A child-friendly module for learning important Quran words and simple meanings.',
    icon_key: 'menu_book',
    status: 'active',
    sort_order: '50',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Module title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 16) nextErrors.description = 'Description should explain the module clearly.';
    const sortOrder = Number(values.sort_order);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedModule = await saveLearningModule({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        icon_key: values.icon_key.trim() || null,
        status: values.status as KidsLearningModule['status'],
        sort_order: sortOrder,
      });
      addOrReplaceModule(savedModule);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const activeModules = modules.filter((module) => module.status === 'active').length || rows.filter((row) => row[2] === 'active').length;
  const comingSoonModules = modules.filter((module) => module.status === 'coming_soon').length || rows.filter((row) => row[2] === 'coming soon').length;
  const lockedModules = modules.filter((module) => module.status === 'locked').length || rows.filter((row) => row[2] === 'locked').length;
  const moduleCards = modules.length
    ? modules.map((module) => ({
        title: module.title,
        slug: module.slug,
        status: module.status.replace(/_/g, ' '),
        description: module.description ?? 'No description yet',
        order: module.sort_order,
        icon_key: module.icon_key ?? null,
      }))
    : rows.map(([title, slug, status, description], index) => ({
        title,
        slug,
        status,
        description,
        order: (index + 1) * 10,
        icon_key: null,
      }));

  return (
    <section className="learning-paths-cms module-cms-page">
      <DashboardHeader title="Learning Modules" text="Create the module cards children see on the Knowledge Base page." />
      <SyncNotice label="Learning modules" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article><span>Total modules</span><strong>{modules.length || rows.length}</strong><em>Knowledge cards</em></article>
        <article><span>Active</span><strong>{activeModules}</strong><em>Visible to children</em></article>
        <article><span>Coming soon</span><strong>{comingSoonModules}</strong><em>Roadmap modules</em></article>
        <article className="accent"><span>Locked</span><strong>{lockedModules}</strong><em>Gated modules</em></article>
      </div>
      <div className="path-cms-layout">
        <section className="cms-panel path-registry-panel module-registry-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Module Registry</p>
              <h2>Knowledge-base modules</h2>
            </div>
            <span>{moduleCards.length} modules</span>
          </div>
          <div className="module-card-grid-cms">
            {moduleCards.map((module) => (
              <article key={module.slug}>
                <div>
                  <span>{module.icon_key ?? module.title.slice(0, 2).toUpperCase()}</span>
                  <StatusBadge value={module.status} />
                </div>
                <h3>{module.title}</h3>
                <p>{module.description}</p>
                <small>{module.slug} - Order {module.order}</small>
              </article>
            ))}
          </div>
        </section>
        <aside className="path-side-panel">
          <form className="cms-editor-section path-editor-card" onSubmit={handleSubmit}>
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>Module Editor</h2>
                <p>Create the child-facing module card and lifecycle status.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label className="wide">Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
              <label className="wide">Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
              <label className="wide">Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
              <label>Icon key<input name="icon_key" onChange={handleChange} value={values.icon_key} /></label>
              <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
              <label className="wide">Status<select name="status" onChange={handleChange} value={values.status}><option value="active">Active</option><option value="coming_soon">Coming soon</option><option value="locked">Locked</option></select></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Learning Module'}</button>
          </form>
          <section className="path-order-panel">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Module Flow</p>
                <h2>Display order</h2>
              </div>
            </div>
            {moduleCards.slice(0, 5).map((module, index) => (
              <div className={index === 0 ? 'active' : ''} key={`module-order-${module.slug}`}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <strong>{module.title}</strong>
              </div>
            ))}
            <p>Drag and drop ordering can be added after edit/detail routes are introduced.</p>
          </section>
        </aside>
      </div>
    </section>
  );
}

function LessonCms() {
  const { addOrReplaceLesson, lessons, rows, state } = useAdminLessonRows();
  const [modules, setModules] = useState<KidsLearningModule[]>([]);
  const [ageGroups, setAgeGroups] = useState<KidsAgeGroup[]>([]);
  const [values, setValues] = useState({
    module_id: '',
    age_group_id: '',
    title: 'Al-Fatihah Key Words',
    slug: 'al-fatihah-key-words',
    summary: 'Learn praise, mercy, worship, and guidance from Surah Al-Fatihah.',
    surah_id: '1',
    ayah_start: '1',
    ayah_end: '7',
    sort_order: '50',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let isMounted = true;
    Promise.all([listAdminLearningModules(), listAgeGroups()])
      .then(([nextModules, nextAgeGroups]) => {
        if (!isMounted) return;
        setModules(nextModules);
        setAgeGroups(nextAgeGroups);
        setValues((current) => ({
          ...current,
          module_id: current.module_id || nextModules[0]?.id || '',
        }));
      })
      .catch(() => {
        if (!isMounted) return;
        setModules([]);
        setAgeGroups([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!values.module_id) nextErrors.module_id = 'Choose a learning module.';
    if (values.title.trim().length < 3) nextErrors.title = 'Lesson title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.summary.trim().length < 16) nextErrors.summary = 'Summary should explain the lesson clearly.';
    const surahId = values.surah_id ? Number(values.surah_id) : null;
    const ayahStart = values.ayah_start ? Number(values.ayah_start) : null;
    const ayahEnd = values.ayah_end ? Number(values.ayah_end) : null;
    const sortOrder = Number(values.sort_order);
    if (surahId !== null && (!Number.isInteger(surahId) || surahId < 1 || surahId > 114)) nextErrors.surah_id = 'Surah must be between 1 and 114.';
    if (ayahStart !== null && (!Number.isInteger(ayahStart) || ayahStart < 1)) nextErrors.ayah_start = 'Ayah start must be positive.';
    if (ayahEnd !== null && (!Number.isInteger(ayahEnd) || (ayahStart !== null && ayahEnd < ayahStart))) nextErrors.ayah_end = 'Ayah end must be after start.';
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedLesson = await saveLesson({
        module_id: values.module_id,
        age_group_id: values.age_group_id || null,
        title: values.title.trim(),
        slug: values.slug.trim(),
        summary: values.summary.trim(),
        surah_id: surahId,
        ayah_start: ayahStart,
        ayah_end: ayahEnd,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
      });
      addOrReplaceLesson(savedLesson);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const activeLessons = lessons.filter((lesson) => lesson.is_active).length || rows.filter((row) => row[3] === 'Active').length;
  const inactiveLessons = lessons.filter((lesson) => !lesson.is_active).length || rows.filter((row) => row[3] === 'Inactive').length;
  const quranLinkedLessons = lessons.filter((lesson) => lesson.surah_id).length || rows.filter((row) => row[2]?.startsWith('Surah')).length;
  const selectedModule = modules.find((module) => module.id === values.module_id) ?? null;
  const selectedAgeGroup = ageGroups.find((ageGroup) => ageGroup.id === values.age_group_id) ?? null;
  const lessonCards = lessons.length
    ? lessons.map((lesson) => ({
        title: lesson.title,
        slug: lesson.slug,
        summary: lesson.summary ?? 'No summary yet',
        quranRange: lesson.surah_id ? `Surah ${lesson.surah_id}${lesson.ayah_start ? `:${lesson.ayah_start}` : ''}${lesson.ayah_end ? `-${lesson.ayah_end}` : ''}` : 'No Quran range',
        status: lesson.is_active ? 'Active' : 'Inactive',
        order: lesson.sort_order,
      }))
    : rows.map(([title, slug, quranRange, status], index) => ({
        title,
        slug,
        summary: quranRange,
        quranRange,
        status,
        order: (index + 1) * 10,
      }));
  const readinessChecks = [
    { label: 'Module selected', ready: Boolean(values.module_id) },
    { label: 'Title', ready: values.title.trim().length >= 3 },
    { label: 'Summary', ready: values.summary.trim().length >= 16 },
    { label: 'Quran range valid', ready: !errors.surah_id && !errors.ayah_start && !errors.ayah_end },
    { label: 'Sort order', ready: Number.isInteger(Number(values.sort_order)) && Number(values.sort_order) >= 0 },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms lesson-cms-page">
      <DashboardHeader title="Lessons CMS" text="Create short lesson records that appear in the child Knowledge Base." />
      <SyncNotice label="Lessons" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article><span>Total lessons</span><strong>{lessons.length || rows.length}</strong><em>Knowledge records</em></article>
        <article><span>Active</span><strong>{activeLessons}</strong><em>Visible to children</em></article>
        <article><span>Quran linked</span><strong>{quranLinkedLessons}</strong><em>Surah or ayah range</em></article>
        <article className="accent"><span>Modules</span><strong>{modules.length}</strong><em>Available parents</em></article>
      </div>
      <div className="lesson-cms-layout">
        <section className="cms-panel lesson-registry-panel">
          <div className="cms-panel-heading">
            <div>
              <p className="eyebrow">Lesson Registry</p>
              <h2>Current lesson records</h2>
            </div>
            <span>{inactiveLessons ? `${inactiveLessons} inactive` : 'All active'}</span>
          </div>
          <div className="lesson-table-cms">
            <div className="lesson-row-cms header">
              <span>Lesson</span>
              <span>Quran range</span>
              <span>Order</span>
              <span>Status</span>
            </div>
            {lessonCards.map((lesson) => (
              <div className="lesson-row-cms" key={lesson.slug}>
                <span><strong>{lesson.title}</strong><small>{lesson.slug}</small></span>
                <span>{lesson.quranRange}</span>
                <span>{lesson.order}</span>
                <span><StatusBadge value={lesson.status} /></span>
              </div>
            ))}
          </div>
        </section>
        <aside className="lesson-side-panel">
          <form className="cms-editor-section lesson-editor-card" onSubmit={handleSubmit}>
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>Lesson Editor</h2>
                <p>Attach a lesson to a module, age group, Quran range, and display order.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label>Module<select name="module_id" onChange={handleChange} value={values.module_id}><option value="">Choose module</option>{modules.map((module) => <option key={module.id} value={module.id}>{module.title}</option>)}</select>{errors.module_id ? <span className="field-error">{errors.module_id}</span> : null}</label>
              <label>Age group<select name="age_group_id" onChange={handleChange} value={values.age_group_id}><option value="">All age groups</option>{ageGroups.map((ageGroup) => <option key={ageGroup.id} value={ageGroup.id}>{ageGroup.label}</option>)}</select></label>
              <label className="wide">Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
              <label className="wide">Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
              <label className="wide">Summary<textarea name="summary" onChange={handleChange} value={values.summary} />{errors.summary ? <span className="field-error">{errors.summary}</span> : null}</label>
              <label>Surah ID<input name="surah_id" onChange={handleChange} type="number" value={values.surah_id} />{errors.surah_id ? <span className="field-error">{errors.surah_id}</span> : null}</label>
              <label>Ayah start<input name="ayah_start" onChange={handleChange} type="number" value={values.ayah_start} />{errors.ayah_start ? <span className="field-error">{errors.ayah_start}</span> : null}</label>
              <label>Ayah end<input name="ayah_end" onChange={handleChange} type="number" value={values.ayah_end} />{errors.ayah_end ? <span className="field-error">{errors.ayah_end}</span> : null}</label>
              <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
              <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Lesson'}</button>
          </form>
          <section className="lesson-preview-card">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Child Preview</p>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
              <StatusBadge value={values.is_active === 'true' ? 'Active' : 'Inactive'} />
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
            <article>
              <small>{selectedModule?.title ?? 'Choose module'} - {selectedAgeGroup?.label ?? 'All ages'}</small>
              <h3>{values.title}</h3>
              <p>{values.summary}</p>
              <div>
                <span>Surah {values.surah_id || 'N/A'}</span>
                <span>Ayah {values.ayah_start || '?'}-{values.ayah_end || '?'}</span>
              </div>
            </article>
          </section>
        </aside>
      </div>
    </section>
  );
}

function AvatarCms() {
  const { addOrReplaceAvatar, avatars, state } = useAdminAvatarRows();
  const { items: mediaAssets } = useAdminMediaRows();
  const [ageGroups, setAgeGroups] = useState<KidsAgeGroup[]>([]);
  const [values, setValues] = useState({
    title: 'Ruby Reader',
    slug: 'ruby-reader',
    age_group_id: '',
    media_id: '',
    color_key: 'ruby',
    sort_order: '40',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let isMounted = true;
    listAgeGroups()
      .then((items) => {
        if (isMounted) setAgeGroups(items);
      })
      .catch(() => {
        if (isMounted) setAgeGroups([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Avatar title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    const sortOrder = Number(values.sort_order);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedAvatar = await saveAvatar({
        title: values.title.trim(),
        slug: values.slug.trim(),
        age_group_id: values.age_group_id || null,
        media_id: values.media_id || null,
        color_key: values.color_key.trim() || null,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
      });
      addOrReplaceAvatar(savedAvatar);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const avatarMedia = mediaAssets.filter((asset) => asset.category === 'avatar' || asset.category === 'website');
  const activeAvatars = avatars.filter((avatar) => avatar.is_active !== false).length;
  const inactiveAvatars = avatars.filter((avatar) => avatar.is_active === false).length;
  const nextSortOrder = avatars.length ? Math.max(...avatars.map((avatar) => avatar.sort_order)) + 10 : 10;
  const selectedAgeGroup = ageGroups.find((ageGroup) => ageGroup.id === values.age_group_id);
  const selectedMedia = avatarMedia.find((asset) => asset.id === values.media_id);
  const selectedMediaUrl = selectedMedia ? mediaImageUrl(selectedMedia) : undefined;
  const fallbackInitial = values.title.trim().slice(0, 1).toUpperCase() || 'A';
  const avatarCards = avatars.length
    ? avatars
    : [
        {
          id: 'fallback-ayah-reader',
          title: 'Ayah Reader',
          slug: 'ayah-reader',
          age_group_id: null,
          media_id: null,
          color_key: 'emerald',
          sort_order: 10,
          is_active: true,
        } satisfies KidsAvatar,
      ];
  const readinessChecks = [
    { label: 'Kid-friendly title', ready: values.title.trim().length >= 3 },
    { label: 'Clean URL slug', ready: /^[a-z0-9-]+$/.test(values.slug) },
    { label: 'Profile image selected', ready: Boolean(values.media_id) },
    { label: 'Color key mapped', ready: Boolean(values.color_key.trim()) },
    { label: 'Onboarding visibility set', ready: Boolean(values.is_active) },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms avatar-cms-page">
      <DashboardHeader title="Avatar CMS" text="Create kid-safe avatar choices for onboarding, learner profiles, and leaderboard identity." />
      <SyncNotice label="Avatars" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article className="accent"><span>Total avatars</span><strong>{avatars.length}</strong><em>Available choices</em></article>
        <article><span>Active</span><strong>{activeAvatars}</strong><em>Visible to children</em></article>
        <article><span>Inactive</span><strong>{inactiveAvatars}</strong><em>Hidden from onboarding</em></article>
        <article><span>Next order</span><strong>{nextSortOrder}</strong><em>Suggested sort value</em></article>
      </div>

      <div className="avatar-cms-layout">
        <section className="cms-panel avatar-library-panel">
          <div className="cms-panel-heading">
            <div>
              <span>Avatar Library</span>
              <h2>Current child profile options</h2>
            </div>
            <span>{avatarCards.length} records</span>
          </div>

          <div className="avatar-card-grid-cms">
            {avatarCards.map((avatar) => {
              const media = avatarMedia.find((asset) => asset.id === avatar.media_id);
              const imageUrl = media ? mediaImageUrl(media) : undefined;
              const ageGroup = ageGroups.find((group) => group.id === avatar.age_group_id);
              return (
                <article key={avatar.id}>
                  <div className="avatar-card-preview">
                    {imageUrl ? <img src={imageUrl} alt={media?.alt_text ?? avatar.title} /> : <span>{avatar.title.slice(0, 1)}</span>}
                  </div>
                  <div>
                    <div className="path-card-heading">
                      <h3>{avatar.title}</h3>
                      <span className={`status-chip ${avatar.is_active === false ? 'draft' : 'published'}`}>{avatar.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                    <p>{ageGroup?.label ?? 'All age groups'} can select this avatar during child profile setup.</p>
                    <div className="path-card-meta">
                      <span>{avatar.slug}</span>
                      <span>{avatar.color_key ?? 'No color key'}</span>
                      <span>Order {avatar.sort_order}</span>
                      <span>{media?.filename ?? 'No media'}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="avatar-side-panel">
          <form className="auth-card wide avatar-editor-card" onSubmit={handleSubmit}>
            <p className="eyebrow">Avatar Builder</p>
            <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
            <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
            <label>Age group<select name="age_group_id" onChange={handleChange} value={values.age_group_id}><option value="">All age groups</option>{ageGroups.map((ageGroup) => <option key={ageGroup.id} value={ageGroup.id}>{ageGroup.label}</option>)}</select></label>
            <label>Avatar image<select name="media_id" onChange={handleChange} value={values.media_id}><option value="">No avatar image</option>{avatarMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
            <label>Color key<input name="color_key" onChange={handleChange} value={values.color_key} /></label>
            <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
            <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Avatar'}</button>
          </form>

          <section className="avatar-preview-card">
            <div className="cms-panel-heading">
              <div>
                <span>Child Preview</span>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
            </div>
            <div className="avatar-profile-preview">
              <div className="avatar-preview-orb">
                {selectedMediaUrl ? <img src={selectedMediaUrl} alt={selectedMedia?.alt_text ?? values.title} /> : <span>{fallbackInitial}</span>}
              </div>
              <strong>{values.title}</strong>
              <p>{selectedAgeGroup?.label ?? 'All age groups'} - {values.color_key || 'No color key'} - {values.is_active === 'true' ? 'Visible' : 'Hidden'}</p>
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function BadgeRewardCms() {
  const { badges, state, addOrReplaceBadge } = useAdminBadgeRows();
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    title: 'Kind Quran Helper',
    slug: 'kind-quran-helper',
    description: 'Awarded when a child completes a helpful practice session with a parent.',
    icon_key: 'sparkles',
    media_id: '',
    sort_order: '40',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Badge title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 16) nextErrors.description = 'Badge description should explain how it is earned.';
    const sortOrder = Number(values.sort_order);
    if (!Number.isInteger(sortOrder) || sortOrder < 0) nextErrors.sort_order = 'Sort order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedBadge = await saveBadge({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        icon_key: values.icon_key.trim() || null,
        media_id: values.media_id || null,
        sort_order: sortOrder,
        is_active: values.is_active === 'true',
      });
      addOrReplaceBadge(savedBadge);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const badgeMedia = mediaAssets.filter((asset) => asset.category === 'badge' || asset.category === 'reward' || asset.category === 'website');
  const activeBadges = badges.filter((badge) => badge.is_active !== false).length;
  const inactiveBadges = badges.filter((badge) => badge.is_active === false).length;
  const imageBackedBadges = badges.filter((badge) => Boolean(badge.media_id)).length;
  const nextSortOrder = badges.length ? Math.max(...badges.map((badge) => badge.sort_order)) + 10 : 10;
  const selectedMedia = badgeMedia.find((asset) => asset.id === values.media_id);
  const selectedMediaUrl = selectedMedia ? mediaImageUrl(selectedMedia) : undefined;
  const badgeCards = badges.length
    ? badges
    : rewardCards.map(([title, text, status], index) => ({
        id: `fallback-badge-${index}`,
        title,
        slug: toSlug(title),
        description: text,
        icon_key: index === 0 ? 'star' : index === 1 ? 'target' : 'sparkles',
        media_id: null,
        sort_order: (index + 1) * 10,
        is_active: status !== 'Locked',
      } satisfies KidsBadge));
  const readinessChecks = [
    { label: 'Achievement title ready', ready: values.title.trim().length >= 3 },
    { label: 'Clean badge slug', ready: /^[a-z0-9-]+$/.test(values.slug) },
    { label: 'Earn rule described', ready: values.description.trim().length >= 16 },
    { label: 'Icon or media selected', ready: Boolean(values.icon_key.trim() || values.media_id) },
    { label: 'Child visibility set', ready: Boolean(values.is_active) },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms badge-cms-page">
      <DashboardHeader title="Badges CMS" text="Create Quran learning achievements children unlock through quizzes, lessons, streaks, and parent-guided practice." />
      <SyncNotice label="Badges" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article className="accent"><span>Total badges</span><strong>{badges.length}</strong><em>Achievement records</em></article>
        <article><span>Active</span><strong>{activeBadges}</strong><em>Shown in rewards</em></article>
        <article><span>Inactive</span><strong>{inactiveBadges}</strong><em>Hidden from children</em></article>
        <article><span>With images</span><strong>{imageBackedBadges}</strong><em>Media mapped</em></article>
        <article><span>Next order</span><strong>{nextSortOrder}</strong><em>Suggested sort</em></article>
      </div>

      <div className="badge-cms-layout">
        <section className="cms-panel badge-library-panel">
          <div className="cms-panel-heading">
            <div>
              <span>Badge Library</span>
              <h2>Achievements children can earn</h2>
            </div>
            <span>{badgeCards.length} records</span>
          </div>

          <div className="badge-card-grid-cms">
            {badgeCards.map((badge) => {
              const media = badgeMedia.find((asset) => asset.id === badge.media_id);
              const imageUrl = media ? mediaImageUrl(media) : undefined;
              return (
                <article key={badge.slug}>
                  <div className="badge-card-medal">
                    {imageUrl ? <img src={imageUrl} alt={media?.alt_text ?? badge.title} /> : <span>{badge.icon_key ?? badge.title.slice(0, 1)}</span>}
                  </div>
                  <div>
                    <div className="path-card-heading">
                      <h3>{badge.title}</h3>
                      <span className={`status-chip ${badge.is_active === false ? 'draft' : 'published'}`}>{badge.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                    <p>{badge.description}</p>
                    <div className="path-card-meta">
                      <span>{badge.slug}</span>
                      <span>{badge.icon_key ?? 'No icon key'}</span>
                      <span>Order {badge.sort_order}</span>
                      <span>{media?.filename ?? 'No media'}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="badge-side-panel">
          <form className="auth-card wide badge-editor-card" onSubmit={handleSubmit}>
            <p className="eyebrow">Badge Builder</p>
            <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
            <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
            <label>Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
            <label>Icon key<input name="icon_key" onChange={handleChange} value={values.icon_key} /></label>
            <label>Badge image<select name="media_id" onChange={handleChange} value={values.media_id}><option value="">No badge image</option>{badgeMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
            <label>Sort order<input name="sort_order" onChange={handleChange} type="number" value={values.sort_order} />{errors.sort_order ? <span className="field-error">{errors.sort_order}</span> : null}</label>
            <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Badge'}</button>
          </form>

          <section className="badge-preview-card">
            <div className="cms-panel-heading">
              <div>
                <span>Reward Preview</span>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
            </div>
            <div className="badge-child-preview">
              <div className="badge-preview-medal">
                {selectedMediaUrl ? <img src={selectedMediaUrl} alt={selectedMedia?.alt_text ?? values.title} /> : <span>{values.icon_key || 'star'}</span>}
              </div>
              <strong>{values.title}</strong>
              <p>{values.description}</p>
              <span>{values.is_active === 'true' ? 'Visible in child rewards' : 'Hidden from children'}</span>
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function RewardItemCms() {
  const { addOrReplaceRewardItem, items, rows, state } = useAdminRewardItemRows();
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    title: 'Surah Reflection Journal',
    slug: 'surah-reflection-journal',
    description: 'A cheerful reward item children can unlock with stars after consistent Quran quiz practice.',
    cost_stars: '125',
    icon_key: 'moon_lamp',
    media_id: '',
    is_active: 'true',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Reward title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 16) nextErrors.description = 'Description should explain the reward clearly.';
    const costStars = Number(values.cost_stars);
    if (!Number.isInteger(costStars) || costStars < 0) nextErrors.cost_stars = 'Cost must be zero or a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedItem = await saveRewardItem({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        cost_stars: costStars,
        icon_key: values.icon_key.trim() || null,
        media_id: values.media_id || null,
        is_active: values.is_active === 'true',
      });
      addOrReplaceRewardItem(savedItem);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const rewardMedia = mediaAssets.filter((asset) => asset.category === 'reward' || asset.category === 'badge' || asset.category === 'website');
  const activeItems = items.filter((item) => item.is_active !== false).length;
  const inactiveItems = items.filter((item) => item.is_active === false).length;
  const imageBackedItems = items.filter((item) => Boolean(item.media_id)).length;
  const lowestCost = items.length ? Math.min(...items.map((item) => item.cost_stars)) : 0;
  const highestCost = items.length ? Math.max(...items.map((item) => item.cost_stars)) : Number(values.cost_stars) || 0;
  const selectedMedia = rewardMedia.find((asset) => asset.id === values.media_id);
  const selectedMediaUrl = selectedMedia ? mediaImageUrl(selectedMedia) : undefined;
  const rewardCards = items.length
    ? items
    : rows.map(([title, cost, description, status], index) => ({
        id: `fallback-reward-${index}`,
        title,
        slug: toSlug(title),
        description,
        cost_stars: Number(cost.replace(/\D/g, '')) || (index + 1) * 75,
        icon_key: index === 0 ? 'cards' : 'checklist',
        media_id: null,
        media: null,
        is_active: status !== 'Inactive',
        created_at: '',
      } satisfies KidsRewardItem));
  const readinessChecks = [
    { label: 'Reward title ready', ready: values.title.trim().length >= 3 },
    { label: 'Clean reward slug', ready: /^[a-z0-9-]+$/.test(values.slug) },
    { label: 'Unlock value described', ready: values.description.trim().length >= 16 },
    { label: 'Star cost configured', ready: Number.isInteger(Number(values.cost_stars)) && Number(values.cost_stars) >= 0 },
    { label: 'Icon or media selected', ready: Boolean(values.icon_key.trim() || values.media_id) },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms reward-cms-page">
      <DashboardHeader title="Reward Items CMS" text="Create star-shop rewards children unlock after Quran quizzes, lesson milestones, and consistent practice." />
      <SyncNotice label="Reward items" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article className="accent"><span>Total rewards</span><strong>{items.length || rows.length}</strong><em>Star shop records</em></article>
        <article><span>Active</span><strong>{activeItems || rows.length}</strong><em>Visible to children</em></article>
        <article><span>Inactive</span><strong>{inactiveItems}</strong><em>Hidden rewards</em></article>
        <article><span>Cost range</span><strong>{lowestCost}-{highestCost}</strong><em>Stars</em></article>
        <article><span>With images</span><strong>{imageBackedItems}</strong><em>Media mapped</em></article>
      </div>

      <div className="reward-cms-layout">
        <section className="cms-panel reward-library-panel">
          <div className="cms-panel-heading">
            <div>
              <span>Reward Shop</span>
              <h2>Unlockable learner rewards</h2>
            </div>
            <span>{rewardCards.length} records</span>
          </div>
          <div className="reward-card-grid-cms">
            {rewardCards.map((item) => {
              const media = rewardMedia.find((asset) => asset.id === item.media_id);
              const imageUrl = media ? mediaImageUrl(media) : undefined;
              return (
                <article key={item.slug}>
                  <div className="reward-card-object">
                    {imageUrl ? <img src={imageUrl} alt={media?.alt_text ?? item.title} /> : <span>{item.icon_key ?? item.title.slice(0, 1)}</span>}
                  </div>
                  <div>
                    <div className="path-card-heading">
                      <h3>{item.title}</h3>
                      <span className={`status-chip ${item.is_active === false ? 'draft' : 'published'}`}>{item.is_active === false ? 'Inactive' : 'Active'}</span>
                    </div>
                    <p>{item.description ?? 'No description yet.'}</p>
                    <div className="path-card-meta">
                      <span>{item.slug}</span>
                      <span>{item.cost_stars} stars</span>
                      <span>{item.icon_key ?? 'No icon key'}</span>
                      <span>{media?.filename ?? 'No media'}</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <aside className="reward-side-panel">
          <form className="auth-card wide reward-editor-card" onSubmit={handleSubmit}>
            <p className="eyebrow">Reward Builder</p>
            <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
            <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
            <label>Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
            <label>Cost in stars<input name="cost_stars" onChange={handleChange} type="number" value={values.cost_stars} />{errors.cost_stars ? <span className="field-error">{errors.cost_stars}</span> : null}</label>
            <label>Icon key<input name="icon_key" onChange={handleChange} value={values.icon_key} /></label>
            <label>Reward image<select name="media_id" onChange={handleChange} value={values.media_id}><option value="">No reward image</option>{rewardMedia.map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
            <label>Status<select name="is_active" onChange={handleChange} value={values.is_active}><option value="true">Active</option><option value="false">Inactive</option></select></label>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Reward Item'}</button>
          </form>

          <section className="reward-preview-card">
            <div className="cms-panel-heading">
              <div>
                <span>Shop Preview</span>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
            </div>
            <div className="reward-child-preview">
              <div className="reward-preview-object">
                {selectedMediaUrl ? <img src={selectedMediaUrl} alt={selectedMedia?.alt_text ?? values.title} /> : <span>{values.icon_key || 'gift'}</span>}
              </div>
              <strong>{values.title}</strong>
              <p>{values.description}</p>
              <span>{values.cost_stars || 0} stars to unlock</span>
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function FeatureFlagCms() {
  const { addOrReplaceFlag, flags, rows, state } = useAdminFeatureFlagRows();
  const [values, setValues] = useState({
    title: 'Guided Recitation',
    slug: 'guided-recitation',
    description: 'A planned learning feature for guided recitation practice and parent review.',
    status: 'coming_soon',
    launch_order: '50',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (values.title.trim().length < 3) nextErrors.title = 'Feature title must be at least 3 characters.';
    if (!/^[a-z0-9-]+$/.test(values.slug)) nextErrors.slug = 'Slug can only use lowercase letters, numbers, and hyphens.';
    if (values.description.trim().length < 16) nextErrors.description = 'Description should explain the feature clearly.';
    const launchOrder = Number(values.launch_order);
    if (!Number.isInteger(launchOrder) || launchOrder < 0) nextErrors.launch_order = 'Launch order must be a positive whole number.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      const savedFlag = await saveFeatureFlag({
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim(),
        status: values.status as KidsFeatureFlag['status'],
        launch_order: launchOrder,
      });
      addOrReplaceFlag(savedFlag);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  const activeFlags = flags.filter((flag) => flag.status === 'active').length;
  const comingSoonFlags = flags.filter((flag) => flag.status === 'coming_soon').length;
  const hiddenFlags = flags.filter((flag) => flag.status === 'hidden').length;
  const nextLaunchOrder = flags.length ? Math.max(...flags.map((flag) => flag.launch_order)) + 10 : Number(values.launch_order) + 10;
  const flagCards = flags.length
    ? flags
    : rows.map(([title, slug, status, description], index) => ({
        id: `fallback-flag-${index}`,
        title,
        slug,
        description,
        status: status.includes('active') ? 'active' : status.includes('hidden') ? 'hidden' : 'coming_soon',
        launch_order: (index + 1) * 10,
        created_at: '',
      } satisfies KidsFeatureFlag));
  const readinessChecks = [
    { label: 'Feature title ready', ready: values.title.trim().length >= 3 },
    { label: 'Clean feature slug', ready: /^[a-z0-9-]+$/.test(values.slug) },
    { label: 'Roadmap description', ready: values.description.trim().length >= 16 },
    { label: 'Launch order configured', ready: Number.isInteger(Number(values.launch_order)) && Number(values.launch_order) >= 0 },
    { label: 'Visibility status selected', ready: ['active', 'coming_soon', 'hidden'].includes(values.status) },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  return (
    <section className="learning-paths-cms feature-cms-page">
      <DashboardHeader title="Feature Flags CMS" text="Control roadmap visibility and rollout status for Kids features without changing code." />
      <SyncNotice label="Feature flags" state={state} />
      <div className="question-bank-metrics path-cms-metrics">
        <article className="accent"><span>Total flags</span><strong>{flags.length || rows.length}</strong><em>Roadmap controls</em></article>
        <article><span>Active</span><strong>{activeFlags}</strong><em>Live features</em></article>
        <article><span>Coming soon</span><strong>{comingSoonFlags || rows.length}</strong><em>Roadmap visible</em></article>
        <article><span>Hidden</span><strong>{hiddenFlags}</strong><em>Internal only</em></article>
        <article><span>Next order</span><strong>{nextLaunchOrder}</strong><em>Suggested launch</em></article>
      </div>

      <div className="feature-cms-layout">
        <section className="cms-panel feature-roadmap-panel">
          <div className="cms-panel-heading">
            <div>
              <span>Roadmap</span>
              <h2>Feature rollout controls</h2>
            </div>
            <span>{flagCards.length} records</span>
          </div>
          <div className="feature-flag-grid-cms">
            {flagCards.map((flag) => (
              <article className={flag.status} key={flag.slug}>
                <div>
                  <span className={`status-chip ${flag.status === 'active' ? 'published' : flag.status === 'hidden' ? 'draft' : 'review'}`}>{flag.status.replace(/_/g, ' ')}</span>
                  <strong>#{flag.launch_order}</strong>
                </div>
                <h3>{flag.title}</h3>
                <p>{flag.description ?? 'No description yet.'}</p>
                <small>{flag.slug}</small>
              </article>
            ))}
          </div>
        </section>

        <aside className="feature-side-panel">
          <form className="auth-card wide feature-editor-card" onSubmit={handleSubmit}>
            <p className="eyebrow">Feature Builder</p>
            <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
            <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
            <label>Description<textarea name="description" onChange={handleChange} value={values.description} />{errors.description ? <span className="field-error">{errors.description}</span> : null}</label>
            <label>Status<select name="status" onChange={handleChange} value={values.status}><option value="active">Active</option><option value="coming_soon">Coming soon</option><option value="hidden">Hidden</option></select></label>
            <label>Launch order<input name="launch_order" onChange={handleChange} type="number" value={values.launch_order} />{errors.launch_order ? <span className="field-error">{errors.launch_order}</span> : null}</label>
            <SaveNotice error={saveError} state={saveState} />
            <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Feature Flag'}</button>
          </form>

          <section className="feature-preview-card">
            <div className="cms-panel-heading">
              <div>
                <span>Roadmap Preview</span>
                <h2>{readyCount}/{readinessChecks.length} checks</h2>
              </div>
            </div>
            <div className={`feature-public-preview ${values.status}`}>
              <span>{values.status.replace(/_/g, ' ')}</span>
              <strong>{values.title}</strong>
              <p>{values.description}</p>
              <small>Launch order #{values.launch_order}</small>
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </section>
  );
}

function KnowledgeBaseCms() {
  const { articles, rows, state } = useAdminArticleRows();
  const publishedCount = articles.filter((article) => article.status === 'published').length || rows.filter((row) => row[2] === 'published').length;
  const draftCount = articles.filter((article) => article.status === 'draft').length || rows.filter((row) => row[2] === 'draft').length;
  const archivedCount = articles.filter((article) => article.status === 'archived').length || rows.filter((row) => row[2] === 'archived').length;
  const contentRows = articles.length
    ? articles.map((article) => ({
        title: article.title,
        slug: article.slug,
        summary: article.summary ?? 'No summary yet',
        status: article.status,
        module: article.module_id ? 'Module linked' : 'Knowledge base',
        support: [
          article.arabic_text ? 'Arabic' : '',
          article.translation ? 'Translation' : '',
          article.child_friendly_explanation ? 'Explanation' : '',
        ].filter(Boolean).join(', ') || 'Needs content',
      }))
    : rows.map(([title, slug, status, summary]) => ({
        title,
        slug,
        summary,
        status,
        module: 'Knowledge base',
        support: summary,
      }));

  return (
    <section className="cms-list-page learning-content-page">
      <SyncNotice label="Knowledge Base CMS" state={state} />
      <div className="cms-page-heading">
        <div>
          <p className="eyebrow">Learning Content</p>
          <h1>Content Repository</h1>
          <p>Manage child-friendly Surah lessons, daily duas, prophet lessons, vocabulary, and Islamic basics for the Kids learning experience.</p>
        </div>
        <Link className="button button-primary" to="/kids/admin/articles/new">Create Content</Link>
      </div>
      <div className="question-bank-metrics learning-content-metrics">
        <article><span>Total records</span><strong>{articles.length || rows.length}</strong><em>Articles and lessons</em></article>
        <article><span>Published</span><strong>{publishedCount}</strong><em>Visible to learners</em></article>
        <article><span>Drafts</span><strong>{draftCount}</strong><em>Needs review</em></article>
        <article className="accent"><span>Archived</span><strong>{archivedCount}</strong><em>Hidden from children</em></article>
      </div>
      <section className="cms-filter-panel">
        <label>
          Search
          <input disabled placeholder="Search lessons, duas, prophets..." type="search" />
        </label>
        <label>
          Type
          <select disabled>
            <option>All content</option>
            <option>Daily Dua</option>
            <option>Prophet Lesson</option>
            <option>Surah Lesson</option>
          </select>
        </label>
        <label>
          Status
          <select disabled>
            <option>All statuses</option>
            <option>Draft</option>
            <option>Published</option>
          </select>
        </label>
        <span>Filters are visual in this phase; live repository filtering comes after edit/detail routing.</span>
      </section>
      <section className="cms-panel learning-repository-table">
        <div className="cms-panel-heading">
          <div>
            <p className="eyebrow">Repository</p>
            <h2>Learning records</h2>
          </div>
          <Link to="/kids/admin/articles/new">Add record</Link>
        </div>
        <div className="cms-learning-table">
          <div className="cms-learning-row header">
            <span>Title</span>
            <span>Module</span>
            <span>Support</span>
            <span>Status</span>
          </div>
          {contentRows.map((article) => (
            <div className="cms-learning-row" key={`${article.slug}-${article.title}`}>
              <span><strong>{article.title}</strong><small>{article.slug}</small></span>
              <span>{article.module}</span>
              <span>{article.support}</span>
              <span><StatusBadge value={article.status} /></span>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}

function CreateEditArticle() {
  const { items: mediaAssets } = useAdminMediaRows();
  const [values, setValues] = useState({
    category: 'Surahs',
    title: 'Surah Al-Fatihah',
    slug: 'surah-al-fatihah',
    summary: 'Learn why Al-Fatihah is called The Opening.',
    arabic_text: 'الفاتحة',
    transliteration: 'Al-Fatihah',
    translation: 'The Opening',
    child_friendly_explanation: 'Al-Fatihah teaches us to praise Allah and ask for guidance.',
    cover_media_reference: '',
    cover_media_id: '',
    related_quiz: 'Surah Names Quiz',
    status: 'draft',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const selectedCover = mediaAssets.find((asset) => asset.id === values.cover_media_id) ?? null;
  const readinessChecks = [
    { label: 'Title', ready: Boolean(values.title.trim()) },
    { label: 'Summary', ready: Boolean(values.summary.trim()) },
    { label: 'Child explanation', ready: values.child_friendly_explanation.trim().length >= 20 },
    { label: 'Arabic or translation', ready: Boolean(values.arabic_text.trim() || values.translation.trim()) },
    { label: 'Media optional', ready: true },
  ];
  const readyCount = readinessChecks.filter((check) => check.ready).length;

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: value,
      slug: name === 'title' && !current.slug ? toSlug(value) : current.slug,
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    const slug = toSlug(values.slug || values.title);
    if (!values.title.trim()) nextErrors.title = 'Article title is required.';
    if (!slug) nextErrors.slug = 'Slug is required and must contain letters or numbers.';
    if (!values.summary.trim()) nextErrors.summary = 'Summary is required for article previews.';
    if (values.child_friendly_explanation.trim().length < 20) nextErrors.child_friendly_explanation = 'Explanation must be at least 20 characters.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      await saveLearningArticleDraft({
        title: values.title.trim(),
        slug,
        summary: values.summary.trim(),
        arabic_text: values.arabic_text.trim() || null,
        transliteration: values.transliteration.trim() || null,
        translation: values.translation.trim() || null,
        child_friendly_explanation: values.child_friendly_explanation.trim(),
        cover_media_id: values.cover_media_id || null,
        status: values.status as 'draft' | 'published' | 'archived',
        sort_order: 0,
      });
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState('error');
    }
  }

  return (
    <section className="cms-builder-page learning-editor-page">
      <div className="cms-builder-topbar">
        <Link className="cms-back-link" to="/kids/admin/articles">Back to Content</Link>
        <div>
          <p className="eyebrow">Content Editor</p>
          <h1>Create Learning Content</h1>
          <p>Prepare Quran learning content with Arabic, transliteration, translation, explanation, media, and related quiz context.</p>
        </div>
        <div className="cms-builder-actions">
          <button className="button button-primary" disabled={saveState === 'saving'} form="learning-content-form" type="submit">
            {saveState === 'saving' ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </div>
      <div className="cms-builder-layout learning-editor-layout">
        <form className="cms-builder-form" id="learning-content-form" onSubmit={handleSubmit}>
          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>01</span>
              <div>
                <h2>Content Details</h2>
                <p>Define the content type, title, URL slug, summary, and lifecycle status.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label>Category<input name="category" onChange={handleChange} value={values.category} /></label>
              <label>Status<select name="status" onChange={handleChange} value={values.status}><option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></label>
              <label>Title<input name="title" onChange={handleChange} value={values.title} />{errors.title ? <span className="field-error">{errors.title}</span> : null}</label>
              <label>Slug<input name="slug" onChange={handleChange} value={values.slug} />{errors.slug ? <span className="field-error">{errors.slug}</span> : null}</label>
              <label className="wide">Summary<textarea name="summary" onChange={handleChange} value={values.summary} />{errors.summary ? <span className="field-error">{errors.summary}</span> : null}</label>
            </div>
          </section>
          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>02</span>
              <div>
                <h2>Quran Text and Meaning</h2>
                <p>Add Arabic text, transliteration, translation, and a child-friendly explanation.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label className="wide">Arabic text<input dir="rtl" name="arabic_text" onChange={handleChange} value={values.arabic_text} /></label>
              <label>Transliteration<input name="transliteration" onChange={handleChange} value={values.transliteration} /></label>
              <label>Translation<textarea name="translation" onChange={handleChange} value={values.translation} /></label>
              <label className="wide">Child-friendly explanation<textarea name="child_friendly_explanation" onChange={handleChange} value={values.child_friendly_explanation} />{errors.child_friendly_explanation ? <span className="field-error">{errors.child_friendly_explanation}</span> : null}</label>
            </div>
          </section>
          <section className="cms-editor-section">
            <div className="cms-section-heading">
              <span>03</span>
              <div>
                <h2>Media and Quiz Context</h2>
                <p>Attach repository media and link this content to an existing quiz theme when useful.</p>
              </div>
            </div>
            <div className="form-grid cms-form-grid">
              <label>Cover media reference<input name="cover_media_reference" onChange={handleChange} value={values.cover_media_reference} /></label>
              <label>Cover image<select name="cover_media_id" onChange={handleChange} value={values.cover_media_id}><option value="">No cover image</option>{mediaAssets.filter((asset) => asset.category === 'learning_article' || asset.category === 'website').map((asset) => <option key={asset.id} value={asset.id}>{asset.filename}</option>)}</select></label>
              <label className="wide">Related quiz<input name="related_quiz" onChange={handleChange} value={values.related_quiz} /></label>
            </div>
            <SaveNotice error={saveError} state={saveState} />
            <div className="button-row">
              <Link className="button button-secondary" to="/kids/admin/articles">Back to Articles</Link>
              <button className="button button-primary" disabled={saveState === 'saving'} type="submit">{saveState === 'saving' ? 'Saving...' : 'Save Draft'}</button>
            </div>
          </section>
        </form>
        <aside className="cms-builder-preview learning-preview-panel">
          <section className="cms-readiness-card">
            <div className="cms-panel-heading">
              <div>
                <p className="eyebrow">Readiness</p>
                <h2>{readyCount}/{readinessChecks.length} checks complete</h2>
              </div>
              <StatusBadge value={values.status} />
            </div>
            <div className="cms-readiness-list">
              {readinessChecks.map((check) => (
                <span className={check.ready ? 'ready' : ''} key={check.label}>{check.label}</span>
              ))}
            </div>
          </section>
          <section className="learning-content-preview">
            <div className="learning-preview-media">
              {selectedCover?.public_url ? <img alt={selectedCover.alt_text ?? selectedCover.filename} src={selectedCover.public_url} /> : <span>{values.category.slice(0, 2) || 'LC'}</span>}
            </div>
            <small>{values.category}</small>
            <h2>{values.title}</h2>
            <p>{values.summary}</p>
            <blockquote dir="rtl">{values.arabic_text || 'النص العربي'}</blockquote>
            <strong>{values.translation || 'Translation preview'}</strong>
            <p>{values.child_friendly_explanation}</p>
          </section>
        </aside>
      </div>
    </section>
  );
}

function MediaLibrary() {
  const { items, state } = useAdminMediaRows();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [values, setValues] = useState({
    filename: 'hero-learning-scene.webp',
    path: 'website/hero-learning-scene.webp',
    mime_type: 'image/webp',
    category: 'website',
    alt_text: 'Children learning Quran with colorful quiz cards',
    description: 'Primary website hero image for Alhudah Kids.',
    tags: 'landing, hero, kids',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const displayedRows = items.length
    ? items.map((item) => [
        item.filename,
        item.category,
        item.description ?? item.path,
        item.alt_text ? item.status : 'Needs alt text',
      ])
    : mediaRows;

  function handleChange(event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSaveState('idle');
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setErrors((current) => ({ ...current, file: '' }));
    setSaveState('idle');
    if (!file) return;

    const folder = values.category === 'quiz_cover' ? 'quiz-covers' : values.category === 'avatar' ? 'avatars' : values.category === 'badge' ? 'badges' : values.category;
    setValues((current) => ({
      ...current,
      filename: file.name,
      path: `${folder}/${toSlug(file.name.replace(/\.[^.]+$/, ''))}.${file.name.split('.').pop()?.toLowerCase() ?? 'webp'}`,
      mime_type: file.type || current.mime_type,
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!values.filename.trim()) nextErrors.filename = 'Filename is required.';
    if (!values.path.trim() || !values.path.includes('/')) nextErrors.path = 'Storage path must include a folder and filename.';
    if (!values.mime_type.startsWith('image/')) nextErrors.mime_type = 'Only image media is allowed here.';
    if (selectedFile && !selectedFile.type.startsWith('image/')) nextErrors.file = 'Only image files can be uploaded.';
    if (values.alt_text.trim().length < 12) nextErrors.alt_text = 'Alt text must clearly describe the image.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaveState('saving');
    setSaveError('');
    try {
      if (selectedFile) {
        await uploadMediaAsset({
          file: selectedFile,
          path: values.path.trim(),
          alt_text: values.alt_text.trim(),
          description: values.description.trim() || null,
          category: values.category as KidsMediaAsset['category'],
          tags: values.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
        });
      } else {
        await saveMediaMetadata({
          filename: values.filename.trim(),
          path: values.path.trim(),
          mime_type: values.mime_type,
          alt_text: values.alt_text.trim(),
          description: values.description.trim() || null,
          category: values.category as KidsMediaAsset['category'],
          tags: values.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
          status: 'active',
        });
      }
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  return (
    <MediaLibraryView
      state={state}
      values={values}
      errors={errors}
      saveState={saveState}
      saveError={saveError}
      hasSelectedFile={Boolean(selectedFile)}
      displayedRows={displayedRows}
      onChange={handleChange}
      onFileChange={handleFileChange}
      onSubmit={handleSubmit}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      SaveNotice={SaveNotice}
    />
  );
}

function AdminReports() {
  const { reports, rows, state } = useAdminReportRows();
  const totalAttempts = reports.reduce((sum, report) => sum + report.attempts, 0);
  const totalStars = reports.reduce((sum, report) => sum + report.total_stars, 0);
  const avgAccuracy = reports.length
    ? Math.round(reports.reduce((sum, report) => sum + report.average_accuracy, 0) / reports.length)
    : 0;
  const reviewCount = reports.filter((report) => report.status !== 'Healthy').length;

  return (
    <AdminReportsView
      state={state}
      stats={[['Attempts', `${totalAttempts}`], ['Avg accuracy', `${avgAccuracy}%`], ['Needs review', `${reviewCount}`], ['Stars awarded', `${totalStars}`]]}
      lowAccuracyText={reviewCount ? 'Review explanations and hints for quizzes below 70% accuracy.' : 'No low-accuracy quiz signal yet.'}
      strongPerformerText={reports.find((report) => report.status === 'Healthy')?.quiz_title ?? 'Completed quiz data will identify strong performers.'}
      rewardImpactText={totalStars ? `${totalStars} stars have been awarded through completed quiz attempts.` : 'Stars will appear after children complete quizzes.'}
      rows={rows}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      StatsGrid={StatsGrid}
      AdminTablePage={AdminTablePage}
    />
  );
}

function ModerationReview() {
  const { requests, rows, state, replaceRequest } = useAdminModerationRows();
  const [selectedId, setSelectedId] = useState('');
  const [reviewValues, setReviewValues] = useState<{ status: KidsParentRequest['status']; reviewer_notes: string }>({
    status: 'in_review',
    reviewer_notes: '',
  });
  const [saveState, setSaveState] = useState<AdminSaveState>('idle');
  const [saveError, setSaveError] = useState('');
  const openCount = requests.filter((request) => request.status === 'new' || request.status === 'in_review').length;
  const selectedRequest = requests.find((request) => request.id === selectedId) ?? requests[0] ?? null;

  useEffect(() => {
    if (!selectedId && requests[0]) {
      setSelectedId(requests[0].id);
    }
  }, [requests, selectedId]);

  useEffect(() => {
    if (!selectedRequest) return;
    setReviewValues({
      status: selectedRequest.status,
      reviewer_notes: selectedRequest.reviewer_notes ?? '',
    });
    setSaveState('idle');
    setSaveError('');
  }, [selectedRequest?.id]);

  function handleReviewChange(event: ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setReviewValues((current) => ({ ...current, [name]: value }));
  }

  async function handleReviewSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedRequest) return;

    const notes = reviewValues.reviewer_notes.trim();
    if ((reviewValues.status === 'resolved' || reviewValues.status === 'closed') && notes.length < 10) {
      setSaveError('Add reviewer notes before resolving or closing the request.');
      setSaveState('error');
      return;
    }

    setSaveState('saving');
    setSaveError('');
    try {
      const updatedRequest = await updateAdminParentRequest({
        id: selectedRequest.id,
        status: reviewValues.status,
        reviewer_notes: notes || null,
      });
      replaceRequest(updatedRequest);
      setSaveState('synced');
    } catch (error) {
      setSaveError(getErrorMessage(error));
      setSaveState(state === 'local' ? 'local' : 'error');
    }
  }

  return (
    <ModerationReviewView
      state={state}
      stats={[
        ['Requests', `${requests.length}`],
        ['Open', `${openCount}`],
        ['Resolved', `${requests.filter((request) => request.status === 'resolved').length}`],
        ['Types', `${new Set(requests.map((request) => request.request_type)).size}`],
      ]}
      rows={rows}
      selectedRequestId={selectedRequest?.id ?? ''}
      requestOptions={requests.map((request) => ({
        id: request.id,
        label: `${request.parent_name} - ${request.request_type.replace(/_/g, ' ')}`,
      }))}
      selectedRequestTitle={selectedRequest ? `Review ${selectedRequest.parent_name}'s request.` : 'No parent requests need review.'}
      selectedRequestMessage={selectedRequest ? selectedRequest.message : 'New parent requests will appear here after families submit feedback from the Parent Guide.'}
      selectedRequestContext={selectedRequest?.related_context ?? null}
      reviewStatus={reviewValues.status}
      reviewerNotes={reviewValues.reviewer_notes}
      saveState={saveState}
      saveError={saveError}
      onSelectedRequestChange={(event) => setSelectedId(event.currentTarget.value)}
      onReviewChange={handleReviewChange}
      onSubmit={handleReviewSubmit}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      StatsGrid={StatsGrid}
      AdminTablePage={AdminTablePage}
      SaveNotice={SaveNotice}
    />
  );
}

function AdminAuditLog() {
  const { entries, rows, state } = useAdminAuditRows();
  const updateCount = entries.filter((entry) => entry.action === 'update').length;
  const createCount = entries.filter((entry) => entry.action === 'insert').length;
  const deleteCount = entries.filter((entry) => entry.action === 'delete').length;
  const entityCount = new Set(entries.map((entry) => entry.entity_type)).size;
  const latestEntry = entries[0] ?? null;
  const displayRows = rows.length ? rows : [['No audit entries yet', 'Waiting', 'Audit log', 'Save a CMS or moderation change to create the first row']];

  return (
    <AdminAuditLogView
      state={state}
      stats={[['Entries', `${entries.length}`], ['Updates', `${updateCount}`], ['Creates', `${createCount}`], ['Deletes', `${deleteCount}`]]}
      latestActivityTitle={latestEntry ? `${latestEntry.action} on ${latestEntry.entity_type.replace(/_/g, ' ')}` : 'No audited changes yet.'}
      latestActivityText={latestEntry ? `${getAuditSummary(latestEntry)} at ${formatDateTime(latestEntry.created_at)}.` : 'Audit entries appear after admins save CMS or moderation changes.'}
      entityCount={entityCount}
      rows={displayRows}
      DashboardHeader={DashboardHeader}
      SyncNotice={SyncNotice}
      StatsGrid={StatsGrid}
      AdminTablePage={AdminTablePage}
    />
  );
}
